import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from '@headlessui/react'
import {
  AnimatePresence,
  domAnimation,
  LazyMotion,
  m,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'motion/react'
import {
  ArrowRight,
  ArrowUpRight,
  Combine,
  BrainCircuit,
  Check,
  ChevronDown,
  CircleCheck,
  Database,
  ExternalLink,
  Factory,
  FileCheck2,
  FileSearch,
  Gauge,
  HardHat,
  Inbox,
  Network,
  Pause,
  Play,
  Radar,
  Send,
  ShieldCheck,
  Sparkles,
  Telescope,
  Workflow,
  X,
  Zap,
} from 'lucide-react'
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { FittedPortalCanvas } from './components/FittedPortalCanvas'
import {
  HeroStageSignals,
  KineticSignalField,
  OperatingProofWall,
  PremiumReveal,
} from './components/PremiumMechanics'
import { HeroShader } from './components/HeroShader'
import { PortalPreview } from './components/PortalPreview'
import {
  capabilityTabs,
  insightCards,
  marketFitEnvironments,
  marketFitSignals,
  marketTypes,
  operatingStandards,
  outcomeStandards,
  outputTiles,
  personas,
  signalPills,
} from './content'

const buttonPrimary =
  'inline-flex min-h-[39px] items-center justify-center gap-2 rounded-[8px] bg-[#111] px-5 py-2.5 text-[14px] font-semibold text-white shadow-[0_1px_0_rgba(255,255,255,.12)_inset] transition duration-200 hover:-translate-y-0.5 hover:bg-[#24194f] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#111]'

const buttonLight =
  'inline-flex min-h-[42px] items-center justify-center gap-2 rounded-[8px] bg-[#f6f5f3] px-5 py-2.5 text-[14px] font-semibold text-[#111] transition duration-200 hover:-translate-y-0.5 hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white'

const handoffLabConcepts = [
  'whole-sentence',
  'whole-seal',
  'whole-poster',
  'whole-layers',
  'whole-dossier',
] as const

type HandoffLabConcept = (typeof handoffLabConcepts)[number]

const fitLabConcepts = [
  'fit-manifesto',
  'fit-blindspot',
  'fit-spectrum',
  'fit-collage',
  'fit-ledger',
] as const

type FitLabConcept = (typeof fitLabConcepts)[number]

const fitMetaphorConcepts = [
  'fit-querybreak',
  'fit-strata',
  'fit-peelback',
  'fit-topology',
  'fit-revision',
] as const

type FitMetaphorConcept = (typeof fitMetaphorConcepts)[number]

const fitRecognitionConcepts = [
  'fit-aperture',
  'fit-sentence',
  'fit-weave',
  'fit-index',
  'fit-commonview',
] as const

type FitRecognitionConcept = (typeof fitRecognitionConcepts)[number]

const fitCommonViewConcepts = [
  'fit-common-circles',
  'fit-common-circles-magnetic',
  'fit-common-circles-focus',
  'fit-common-circles-arcs',
  'fit-common-circles-depth',
  'fit-common-circles-flow',
  'fit-common-rail',
  'fit-common-margin',
  'fit-common-fold',
  'fit-common-prism',
  'fit-common-register',
] as const

type FitCommonViewConcept = (typeof fitCommonViewConcepts)[number]

const relayConcepts = [
  'quiet-depth',
  'signal-ribbon',
  'editorial',
  'evidence-lenses',
  'storyboard',
  'open-storyboard',
  'guided-lenses',
  'free-lenses',
  'relay-lenses',
  'vertical-polished',
  'horizontal-polished',
  'distinct-evidence',
  'handoff-docket',
  'handoff-folio',
  'handoff-whole',
  'outcome-led',
  'fit-diagnostic',
  ...fitLabConcepts,
  ...fitMetaphorConcepts,
  ...fitRecognitionConcepts,
  ...fitCommonViewConcepts,
  ...handoffLabConcepts,
] as const

export type RelayConcept = (typeof relayConcepts)[number]

function getRelayConcept(): RelayConcept | 'control' {
  if (typeof window === 'undefined') return 'control'

  const requestedConcept = new URLSearchParams(window.location.search).get('relay')
  return relayConcepts.includes(requestedConcept as RelayConcept)
    ? (requestedConcept as RelayConcept)
    : 'control'
}

const proofCadenceConcepts = [
  'operating-interludes',
  'testimonial-cadence-preview',
] as const

type ProofCadenceConcept = (typeof proofCadenceConcepts)[number]

function getProofCadenceConcept(): ProofCadenceConcept | 'control' {
  if (typeof window === 'undefined') return 'control'

  const requestedConcept = new URLSearchParams(window.location.search).get('proof')
  return proofCadenceConcepts.includes(requestedConcept as ProofCadenceConcept)
    ? (requestedConcept as ProofCadenceConcept)
    : 'control'
}

const proofPreviewSlots = {
  portal: {
    context: 'After the portal',
    headline: 'Approved customer voice about seeing a real operating system will live here.',
    index: '01',
    subject: 'Product substance',
    tone: 'mint',
  },
  commonView: {
    context: 'After the Common View',
    headline: 'Approved customer voice about making specialist judgment reviewable will live here.',
    index: '02',
    subject: 'Shared market view',
    tone: 'violet',
  },
  operating: {
    context: 'After the operating layer',
    headline: 'Approved customer voice about operated delivery will live here.',
    index: '03',
    subject: 'Operating confidence',
    tone: 'coral',
  },
  platform: {
    context: 'After the platform',
    headline: 'Approved customer voice about keeping the evidence chain connected will live here.',
    index: '04',
    subject: 'Connected execution',
    tone: 'mint',
  },
  seller: {
    context: 'After the seller outcome',
    headline: 'Approved customer voice about entering conversations with context will live here.',
    index: '05',
    subject: 'Seller usefulness',
    tone: 'violet',
  },
} as const

type ProofPreviewSlot = keyof typeof proofPreviewSlots

function TestimonialFramePreview({ slot }: { slot: ProofPreviewSlot }) {
  const frame = proofPreviewSlots[slot]
  const alignment = Number(frame.index) % 2 === 0 ? 'align-right' : 'align-left'

  return (
    <aside
      aria-labelledby={`proof-preview-${slot}`}
      className={`testimonial-frame-preview tone-${frame.tone} ${alignment}`}
      data-proof-slot={slot}
    >
      <div aria-hidden="true" className="testimonial-frame-orbit">
        <span>{frame.index}</span>
      </div>
      <div className="testimonial-frame-copy">
        <span className="testimonial-frame-kicker">
          <FileCheck2 aria-hidden="true" />
          CUSTOMER PROOF · DESIGN PLACEHOLDER
        </span>
        <p id={`proof-preview-${slot}`}>{frame.headline}</p>
        <div className="testimonial-frame-attribution">
          <span className="testimonial-frame-avatar" aria-hidden="true">{frame.index}</span>
          <span>
            <b>Approved speaker + role</b>
            <small>Customer and market context required</small>
          </span>
        </div>
      </div>
      <dl className="testimonial-frame-receipt">
        <div><dt>Placement</dt><dd>{frame.context}</dd></div>
        <div><dt>Proof subject</dt><dd>{frame.subject}</dd></div>
        <div><dt>Publication gate</dt><dd>Permission · source · period</dd></div>
      </dl>
    </aside>
  )
}

function CustomerResultsPreview() {
  return (
    <section className="customer-results-preview" data-proof-preview="results">
      <div className="customer-results-preview-heading">
        <span>CUSTOMER RESULTS · DESIGN PLACEHOLDER</span>
        <h2>Real customer results will live here.</h2>
        <p>
          This preserves Amplemarket’s result-first proof chapter without
          inventing a customer, number, quotation, or relationship.
        </p>
      </div>
      <div className="customer-results-preview-grid">
        <article className="is-featured">
          <span>FEATURED CUSTOMER STORY</span>
          <h3>Before state → observable change</h3>
          <p>Approved customer context, what changed, and what Wave99 operated.</p>
          <footer>Source + measurement period required</footer>
        </article>
        <article className="is-metric tone-mint">
          <span>PRIMARY RESULT</span>
          <h3>Measured outcome</h3>
          <p>Approved metric and period</p>
        </article>
        <article className="is-metric tone-violet">
          <span>SUPPORTING RESULT</span>
          <h3>Reviewable change</h3>
          <p>Approved evidence boundary</p>
        </article>
        <article className="is-quote">
          <span>ATTRIBUTED CUSTOMER VOICE</span>
          <h3>Approved quotation will live here.</h3>
          <p>Speaker · role · customer · permission</p>
        </article>
        <article className="is-secondary tone-coral">
          <span>INDEPENDENT STORY</span>
          <h3>Second customer outcome</h3>
          <p>Separate source and attribution required</p>
        </article>
      </div>
    </section>
  )
}

const customerLoveSubjects = [
  'Market reconstruction',
  'Evidence quality',
  'Seller context',
  'Operated delivery',
  'Sending safeguards',
  'Reply handoff',
  'Specialist judgment',
  'Commercial timing',
  'Team confidence',
] as const

function CustomerLovePreview() {
  return (
    <section className="customer-love-preview" data-proof-preview="customer-love">
      <div className="customer-love-preview-heading">
        <span>CUSTOMER LOVE · DESIGN PLACEHOLDER</span>
        <h2>Customer voices, once approved.</h2>
        <p>
          The wall appears only when enough independent, permissioned
          short-form customer evidence exists.
        </p>
      </div>
      <div className="customer-love-preview-grid">
        {customerLoveSubjects.map((subject, index) => (
          <article className={`love-placeholder tone-${(index % 3) + 1}`} key={subject}>
            <div className="love-placeholder-person">
              <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              <span><b>Approved customer voice</b><small>Speaker and role required</small></span>
            </div>
            <p>Customer evidence about {subject.toLowerCase()} will live here.</p>
            <footer>Permission · source · attribution</footer>
          </article>
        ))}
      </div>
    </section>
  )
}

function HandoffLabOutput({ variant }: { variant: HandoffLabConcept }) {
  const integrity = (
    <ul
      aria-label="What arrives with the qualified conversation"
      className="handoff-lab-integrity"
    >
      <li>Source attached</li>
      <li>Reason preserved</li>
      <li>Owner accountable</li>
    </ul>
  )

  if (variant === 'whole-seal') {
    return (
      <div className="handoff-lab-composition handoff-lab-seal">
        <div aria-hidden="true" className="handoff-lab-seal-mark">
          <span><Inbox /></span>
        </div>
        <div className="handoff-lab-seal-copy">
          <small>THE COMMERCIAL OUTPUT</small>
          <h3 aria-label="Qualified conversation arrives whole.">
            <span>Qualified conversation</span>
            <strong>arrives whole.</strong>
          </h3>
          {integrity}
        </div>
      </div>
    )
  }

  if (variant === 'whole-poster') {
    return (
      <div className="handoff-lab-composition handoff-lab-poster">
        <small>THE COMMERCIAL OUTPUT</small>
        <h3 aria-label="Qualified conversation arrives whole.">
          <span>Qualified conversation</span>
          <strong>ARRIVES WHOLE.</strong>
        </h3>
        {integrity}
      </div>
    )
  }

  if (variant === 'whole-layers') {
    return (
      <div className="handoff-lab-composition handoff-lab-layers">
        <div aria-hidden="true" className="handoff-lab-layer-words">
          <span>Source</span>
          <span>Reason</span>
          <span>Owner</span>
        </div>
        <small>THE COMMERCIAL OUTPUT</small>
        <h3 aria-label="Qualified conversation arrives whole.">
          <span>Qualified conversation</span>
          <strong>arrives whole.</strong>
        </h3>
        {integrity}
      </div>
    )
  }

  if (variant === 'whole-dossier') {
    return (
      <div className="handoff-lab-composition handoff-lab-dossier">
        <div className="handoff-lab-dossier-header">
          <small>THE COMMERCIAL OUTPUT</small>
          <span><Inbox aria-hidden="true" />SELLER READY</span>
        </div>
        <h3 aria-label="Qualified conversation arrives whole.">
          <span>Qualified conversation</span>
          <strong>arrives whole.</strong>
        </h3>
        {integrity}
      </div>
    )
  }

  return (
    <div className="handoff-lab-composition handoff-lab-sentence">
      <small>THE COMMERCIAL OUTPUT</small>
      <h3 aria-label="Qualified conversation arrives whole.">
        <span>Qualified conversation</span>
        <strong>arrives whole.</strong>
      </h3>
      {integrity}
    </div>
  )
}

function FitEnvironmentLine({ inverted = false }: { inverted?: boolean }) {
  return (
    <footer className={`fit-lab-environments${inverted ? ' fit-lab-environments-inverted' : ''}`}>
      <span>COMMON ENVIRONMENTS</span>
      <ul aria-label="Common Wave99 market environments">
        {marketFitEnvironments.map((environment) => (
          <li key={environment}>{environment}</li>
        ))}
      </ul>
    </footer>
  )
}

function FitLabOutput({ variant }: { variant: FitLabConcept }) {
  const fitTitle = 'Your market is easy to describe. Difficult to query.'
  const fitSummary =
    'The commercial truth exists, but it is scattered across technical language, projects, certifications, partners, and specialist roles.'
  const fitIcons = [Database, Radar, Network, ShieldCheck]

  if (variant === 'fit-blindspot') {
    return (
      <section
        aria-labelledby="fit-blindspot-title"
        className="market-fit-lab market-fit-blindspot"
        data-fit-style="blindspot"
      >
        <header>
          <small>WAVE99 FITS WHEN</small>
          <h3 id="fit-blindspot-title" aria-label={fitTitle}>
            <span>Your experts can see the market.</span>
            <strong>The database sees only part of it.</strong>
          </h3>
          <p>{fitSummary}</p>
        </header>

        <div className="fit-blindspot-field">
          <div className="fit-blindspot-core">
            <Database aria-hidden="true" />
            <span>
              <small>ORDINARY DATABASE</small>
              <strong>Incomplete market picture</strong>
            </span>
          </div>
          <div className="fit-blindspot-zone">
            <Sparkles aria-hidden="true" />
            <span>
              <small>WAVE99 FIT</small>
              <strong>Commercial truth lives beyond the category label.</strong>
            </span>
          </div>
          <ul>
            {marketFitSignals.map((signal, index) => {
              const Icon = fitIcons[index]
              return (
                <li key={signal.title}>
                  <Icon aria-hidden="true" />
                  <strong>{signal.title}</strong>
                  <p>{signal.detail}</p>
                </li>
              )
            })}
          </ul>
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-spectrum') {
    return (
      <section
        aria-labelledby="fit-spectrum-title"
        className="market-fit-lab market-fit-spectrum"
        data-fit-style="spectrum"
      >
        <header>
          <small>THE FIT SPECTRUM</small>
          <h3 id="fit-spectrum-title" aria-label={fitTitle}>
            <span>Easy to describe.</span>
            <strong>Difficult to query.</strong>
          </h3>
          <p>{fitSummary}</p>
        </header>

        <div className="fit-spectrum-field">
          <div aria-hidden="true" className="fit-spectrum-axis">
            <span>DATABASE-READABLE</span>
            <strong>STRONGER WAVE99 FIT</strong>
          </div>
          <ol>
            {marketFitSignals.map((signal) => (
              <li key={signal.title}>
                <i aria-hidden="true" />
                <strong>{signal.title}</strong>
                <p>{signal.detail}</p>
              </li>
            ))}
          </ol>
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-collage') {
    return (
      <section
        aria-labelledby="fit-collage-title"
        className="market-fit-lab market-fit-collage"
        data-fit-style="collage"
      >
        <header>
          <small>WAVE99 FITS WHEN</small>
          <h3 id="fit-collage-title" aria-label={fitTitle}>
            <span>Your market is easy to describe.</span>
            <strong>Difficult to query.</strong>
          </h3>
          <p>{fitSummary}</p>
        </header>

        <div className="fit-collage-field">
          {marketFitSignals.map((signal, index) => {
            const Icon = fitIcons[index]
            return (
              <article key={signal.title}>
                <header>
                  <span>FIELD NOTE {String(index + 1).padStart(2, '0')}</span>
                  <Icon aria-hidden="true" />
                </header>
                <strong>{signal.title}</strong>
                <p>{signal.detail}</p>
              </article>
            )
          })}
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-ledger') {
    return (
      <section
        aria-labelledby="fit-ledger-title"
        className="market-fit-lab market-fit-ledger"
        data-fit-style="ledger"
      >
        <header>
          <small>MARKET FIT / REVIEW</small>
          <h3 id="fit-ledger-title" aria-label={fitTitle}>
            <span>Easy to describe.</span>
            <strong>Difficult to query.</strong>
          </h3>
          <p>{fitSummary}</p>
        </header>

        <div className="fit-ledger-body">
          <div className="fit-ledger-thesis">
            <small>THE DECIDING CONDITION</small>
            <strong>
              Your team knows the commercial market better than the systems
              meant to describe it.
            </strong>
          </div>
          <dl>
            {marketFitSignals.map((signal, index) => (
              <div key={signal.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <dt>{signal.title}</dt>
                <dd>{signal.detail}</dd>
              </div>
            ))}
          </dl>
        </div>

        <FitEnvironmentLine inverted />
      </section>
    )
  }

  return (
    <section
      aria-labelledby="fit-manifesto-title"
      className="market-fit-lab market-fit-manifesto"
      data-fit-style="manifesto"
    >
      <header>
        <small>WAVE99 FITS WHEN</small>
        <h3 id="fit-manifesto-title" aria-label={fitTitle}>
          <span>Your market is</span>
          <strong>easy to describe.</strong>
          <b>Difficult to query.</b>
        </h3>
        <p>{fitSummary}</p>
      </header>

      <ul className="fit-manifesto-statements">
        {marketFitSignals.map((signal) => (
          <li key={signal.title}>
            <strong>{signal.title}</strong>
            <span>{signal.detail}</span>
          </li>
        ))}
      </ul>

      <FitEnvironmentLine />
    </section>
  )
}

function FitMetaphorOutput({ variant }: { variant: FitMetaphorConcept }) {
  if (variant === 'fit-strata') {
    return (
      <section
        aria-labelledby="fit-strata-title"
        className="market-fit-metaphor market-fit-strata"
        data-fit-metaphor="strata"
      >
        <header>
          <small>MARKET CROSS-SECTION</small>
          <h3
            aria-label="The category is only the surface."
            id="fit-strata-title"
          >
            The category is only <strong>the surface.</strong>
          </h3>
          <p>
            The commercial market sits deeper—in specialist language, live
            timing, ownership routes, and proof boundaries.
          </p>
        </header>

        <div className="fit-strata-section">
          <div className="fit-strata-surface">
            <span><Database aria-hidden="true" />DATABASE LABEL</span>
            <strong>Industrial systems</strong>
          </div>
          <div className="fit-strata-layer fit-strata-language">
            <small>EXPERT LANGUAGE</small>
            <strong>Defines who belongs</strong>
            <span>Terms, roles, routes, exclusions</span>
          </div>
          <div className="fit-strata-layer fit-strata-timing">
            <small>COMMERCIAL TIMING</small>
            <strong>Shows why now</strong>
            <span>Hiring, certifications, projects, upgrades</span>
          </div>
          <div className="fit-strata-layer fit-strata-ownership">
            <small>OWNERSHIP</small>
            <strong>Crosses the record</strong>
            <span>Plants, partners, regions, technical roles</span>
          </div>
          <div className="fit-strata-layer fit-strata-proof">
            <small>PROOF BOUNDARY</small>
            <strong>Constrains the motion</strong>
            <span>Sources, approved claims, accountable owner</span>
          </div>
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-peelback') {
    return (
      <section
        aria-labelledby="fit-peelback-title"
        className="market-fit-metaphor market-fit-peelback"
        data-fit-metaphor="peelback"
      >
        <header>
          <small>THE DATABASE VIEW</small>
          <h3
            aria-label="The rows look complete. The market is missing."
            id="fit-peelback-title"
          >
            The rows look complete.
            <strong>The market is missing.</strong>
          </h3>
        </header>

        <div className="fit-peelback-stage">
          <div aria-hidden="true" className="fit-peelback-grid">
            <div>CATEGORY</div><div>REGION</div><div>SIZE</div>
            <div>Industrial systems</div><div>Europe</div><div>—</div>
            <div>Technical services</div><div>Global</div><div>—</div>
            <div>Specialist manufacturing</div><div>Nordics</div><div>—</div>
            <div>Regulated suppliers</div><div>Multiple</div><div>—</div>
          </div>

          <div className="fit-peelback-reveal">
            <span>WHAT YOUR TEAM KNOWS</span>
            <h4>
              The market is defined by evidence,
              <strong>not a dropdown.</strong>
            </h4>
            <p>
              Specialist language establishes the boundary. Projects and
              certifications reveal timing. Plants and partners reveal
              ownership. Approved proof decides what can be said.
            </p>
            <ul aria-label="Evidence that defines the real market">
              <li>Language + roles</li>
              <li>Projects + upgrades</li>
              <li>Plants + partners</li>
              <li>Proof + timing</li>
            </ul>
          </div>

          <div aria-hidden="true" className="fit-peelback-corner">
            <Database />
            <span>ORDINARY FILTERS</span>
          </div>
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-topology') {
    const topologyNodes = [
      { icon: Factory, label: 'Plant', className: 'fit-topology-plant' },
      { icon: Network, label: 'Specialist partner', className: 'fit-topology-partner' },
      { icon: HardHat, label: 'Technical role', className: 'fit-topology-role' },
      { icon: BrainCircuit, label: 'Buying committee', className: 'fit-topology-committee' },
    ]

    return (
      <section
        aria-labelledby="fit-topology-title"
        className="market-fit-metaphor market-fit-topology"
        data-fit-metaphor="topology"
      >
        <header>
          <small>MARKET TOPOLOGY</small>
          <h3
            aria-label="Your market has a route. Not a row."
            id="fit-topology-title"
          >
            Your market has a route.{' '}
            <strong>Not a row.</strong>
          </h3>
          <p>
            Ownership and opportunity travel across plants, partners, and
            technical roles—while ordinary databases flatten them into one
            account record.
          </p>
        </header>

        <div className="fit-topology-map">
          <svg aria-hidden="true" viewBox="0 0 1180 430">
            <defs>
              <linearGradient id="fit-topology-gradient" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#e56d59" />
                <stop offset="52%" stopColor="#8c5ed1" />
                <stop offset="100%" stopColor="#5eb399" />
              </linearGradient>
            </defs>
            <path className="fit-topology-route-shadow" d="M82 315 C210 314 224 104 380 121 S557 356 710 286 S875 91 1087 128" />
            <path className="fit-topology-route" d="M82 315 C210 314 224 104 380 121 S557 356 710 286 S875 91 1087 128" />
            <path className="fit-topology-branch" d="M380 121 C441 43 557 45 627 92" />
            <path className="fit-topology-branch" d="M710 286 C811 354 953 345 1035 277" />
          </svg>

          <div className="fit-topology-thesis">
            <Radar aria-hidden="true" />
            <p>
              <small>THE WAVE99 FIT</small>
              <strong>
                The opportunity exists between the records.
              </strong>
            </p>
          </div>

          {topologyNodes.map(({ icon: Icon, label, className }) => (
            <div className={`fit-topology-node ${className}`} key={label}>
              <span><Icon aria-hidden="true" /></span>
              <strong>{label}</strong>
            </div>
          ))}

          <div className="fit-topology-signal fit-topology-hiring">
            <small>LIVE SIGNAL</small>
            <strong>Hiring + certification</strong>
          </div>
          <div className="fit-topology-signal fit-topology-project">
            <small>LIVE SIGNAL</small>
            <strong>Project + upgrade</strong>
          </div>
          <div className="fit-topology-proof">
            <ShieldCheck aria-hidden="true" />
            <span>
              <small>PROOF BOUNDARY</small>
              <strong>Approved reason to talk</strong>
            </span>
          </div>
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-revision') {
    return (
      <section
        aria-labelledby="fit-revision-title"
        className="market-fit-metaphor market-fit-revision"
        data-fit-metaphor="revision"
      >
        <div className="fit-revision-sheet">
          <header>
            <div>
              <small>MARKET DEFINITION / REVISION 99</small>
              <span>REVIEW STATUS · OPEN</span>
            </div>
            <h3
              aria-label="The market changed. The record did not."
              id="fit-revision-title"
            >
              The market changed.
              <strong>The record did not.</strong>
            </h3>
          </header>

          <div className="fit-revision-drawing">
            <div className="fit-revision-stored">
              <small>STORED CATEGORY</small>
              <del>Industrial systems</del>
              <span aria-hidden="true">OUTDATED MODEL</span>
            </div>

            <div className="fit-revision-definition">
              <span>REVISED COMMERCIAL DEFINITION</span>
              <strong>
                Accounts your experts recognize,
                with the timing and evidence your sellers can use.
              </strong>
            </div>

            <ol aria-label="Required market-definition revisions">
              <li>
                <span>R1</span>
                Add specialist language and roles
              </li>
              <li>
                <span>R2</span>
                Attach live projects and certifications
              </li>
              <li>
                <span>R3</span>
                Trace plants, partners, and ownership
              </li>
              <li>
                <span>R4</span>
                Keep sources and approved claims attached
              </li>
            </ol>
          </div>

          <footer>
            <span><CircleCheck aria-hidden="true" />REVISED STATE</span>
            <strong>Reviewable market · usable timing · accountable handoff</strong>
          </footer>
        </div>

        <FitEnvironmentLine inverted />
      </section>
    )
  }

  return (
    <section
      aria-labelledby="fit-querybreak-title"
      className="market-fit-metaphor market-fit-querybreak"
      data-fit-metaphor="querybreak"
    >
      <header>
        <small>DATABASE QUERY / LIMIT REACHED</small>
        <h3
          aria-label="Your market fits in a sentence. Not in one filter."
          id="fit-querybreak-title"
        >
          Your market fits in a sentence.
          <strong>Not in one filter.</strong>
        </h3>
        <p>
          Your experts describe the market through language, timing, routes,
          and proof. Ordinary databases ask for a category.
        </p>
      </header>

      <div className="fit-querybreak-stage">
        <div className="fit-querybreak-filter">
          <span><Database aria-hidden="true" />ORDINARY DATABASE</span>
          <p>
            Category
            <strong>Industrial systems</strong>
          </p>
          <small>QUERY BOUNDARY</small>
        </div>

        <p
          aria-label="Plants upgrading regulated equipment through specialist distributors with approved claims."
          className="fit-querybreak-definition"
        >
          <span className="fit-querybreak-entry">
            Plants upgrading
          </span>
          <span className="fit-querybreak-overflow">
            <strong>regulated equipment</strong> through
            {' '}<b>specialist distributors</b> with
            {' '}<em>approved claims.</em>
          </span>
        </p>

        <div className="fit-querybreak-note">
          <ArrowRight aria-hidden="true" />
          <span>
            <small>YOUR TEAM’S DEFINITION</small>
            <strong>Keeps going after the filter ends.</strong>
          </span>
        </div>
      </div>

      <FitEnvironmentLine />
    </section>
  )
}

const fitRecognitionHeading =
  'Your experts recognize the market. Now the whole team can work from it.'

const fitRecognitionSummary =
  'Wave99 turns specialist language and current evidence into a market your team can review together—not another category export.'

function FitRecognitionHeader({ titleId }: { titleId: string }) {
  return (
    <header className="fit-recognition-header">
      <small>BUILT FOR EXPERT-LED MARKETS</small>
      <h3 aria-label={fitRecognitionHeading} id={titleId}>
        <span>Your experts recognize the market.</span>
        <strong>Now the whole team can work from it.</strong>
      </h3>
      <p>{fitRecognitionSummary}</p>
    </header>
  )
}

function FitRecognitionOutput({
  variant,
}: {
  variant: FitRecognitionConcept
}) {
  if (variant === 'fit-sentence') {
    return (
      <section
        aria-labelledby="fit-sentence-title"
        className="market-fit-recognition market-fit-sentence group"
        data-fit-recognition="sentence"
      >
        <FitRecognitionHeader titleId="fit-sentence-title" />

        <div className="fit-sentence-stage">
          <div className="fit-sentence-definition">
            <span>REPRESENTATIVE MARKET DEFINITION</span>
            <p>
              <strong>Specialist manufacturers</strong>{' '}
              entering <b>regulated projects</b>{' '}
              through <em>approved partners.</em>
            </p>
            <i aria-hidden="true" />
          </div>

          <div className="fit-sentence-evidence">
            <span><FileSearch aria-hidden="true" />Language recognized</span>
            <span><Radar aria-hidden="true" />Change evidenced</span>
            <span><ShieldCheck aria-hidden="true" />Claims bounded</span>
          </div>

          <div className="fit-sentence-result">
            <ArrowRight aria-hidden="true" />
            <span>
              <small>SHARED COMMERCIAL SCOPE</small>
              <strong>One market the team can review</strong>
            </span>
          </div>
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-weave') {
    return (
      <section
        aria-labelledby="fit-weave-title"
        className="market-fit-recognition market-fit-weave group"
        data-fit-recognition="weave"
      >
        <FitRecognitionHeader titleId="fit-weave-title" />

        <div className="fit-weave-stage">
          <div className="fit-weave-inputs">
            <span>
              <FileSearch aria-hidden="true" />
              <small>EXPERT DEFINITION</small>
              <strong>Language, roles, exclusions</strong>
            </span>
            <span>
              <Radar aria-hidden="true" />
              <small>CURRENT EVIDENCE</small>
              <strong>Projects, certifications, change</strong>
            </span>
            <span>
              <Network aria-hidden="true" />
              <small>COMMERCIAL CONTEXT</small>
              <strong>Plants, partners, ownership</strong>
            </span>
          </div>

          <div aria-hidden="true" className="fit-weave-threads">
            <i /><i /><i />
          </div>

          <div className="fit-weave-result">
            <Sparkles aria-hidden="true" />
            <span>
              <small>ONE SHARED MARKET</small>
              <strong>Recognizable. Current. Reviewable.</strong>
              <p>Who belongs, what changed, and what the team can stand behind.</p>
            </span>
          </div>
        </div>

        <FitEnvironmentLine inverted />
      </section>
    )
  }

  if (variant === 'fit-index') {
    return (
      <section
        aria-labelledby="fit-index-title"
        className="market-fit-recognition market-fit-index group"
        data-fit-recognition="index"
      >
        <FitRecognitionHeader titleId="fit-index-title" />

        <div className="fit-index-stage">
          <div aria-hidden="true" className="fit-index-focus-line" />
          <div className="fit-index-track fit-index-language">
            <span>technical language</span>
            <span>specialist roles</span>
            <span>explicit exclusions</span>
            <span>buyer vocabulary</span>
          </div>
          <div className="fit-index-track fit-index-evidence">
            <span>projects</span>
            <span>certifications</span>
            <span>plant changes</span>
            <span>procurement activity</span>
          </div>
          <div className="fit-index-track fit-index-context">
            <span>ownership</span>
            <span>approved proof</span>
            <span>partners</span>
            <span>accountable owner</span>
          </div>

          <div className="fit-index-lock">
            <Radar aria-hidden="true" />
            <span>
              <small>MARKET LOCK</small>
              <strong>Expert-recognized. Evidence-linked.</strong>
            </span>
          </div>
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-commonview') {
    return (
      <section
        aria-labelledby="fit-commonview-title"
        className="market-fit-recognition market-fit-commonview group"
        data-fit-recognition="commonview"
      >
        <FitRecognitionHeader titleId="fit-commonview-title" />

        <div className="fit-commonview-stage">
          <div className="fit-commonview-plane fit-commonview-expert">
            <span><BrainCircuit aria-hidden="true" />EXPERT VIEW</span>
            <strong>Accounts your team recognizes</strong>
            <p>Specialist language · roles · routes</p>
          </div>

          <div className="fit-commonview-plane fit-commonview-evidence">
            <span><ShieldCheck aria-hidden="true" />VERIFIABLE VIEW</span>
            <strong>Evidence the team can inspect</strong>
            <p>Current change · source · proof boundary</p>
          </div>

          <div className="fit-commonview-core">
            <Sparkles aria-hidden="true" />
            <span>
              <small>THE COMMON VIEW</small>
              <strong>One reviewable market</strong>
              <p>Clear enough for the whole team to work from.</p>
            </span>
          </div>
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  return (
    <section
      aria-labelledby="fit-aperture-title"
      className="market-fit-recognition market-fit-aperture group"
      data-fit-recognition="aperture"
    >
      <FitRecognitionHeader titleId="fit-aperture-title" />

      <div className="fit-aperture-stage">
        <div aria-hidden="true" className="fit-aperture-records">
          <span>CATEGORY / INDUSTRIAL SYSTEMS</span>
          <span>REGION / NORTHERN EUROPE</span>
          <span>SIZE / 200–500</span>
          <span>TYPE / MANUFACTURER</span>
          <span>RECORD / COMPLETE</span>
        </div>

        <div className="fit-aperture-lens">
          <Radar aria-hidden="true" />
          <span>
            <small>COMMERCIAL FOCUS</small>
            <strong>A reviewable market</strong>
            <p>Defined in your language. Grounded in current evidence.</p>
          </span>
          <ul aria-label="What brings the market into commercial focus">
            <li>Expert-defined</li>
            <li>Evidence-linked</li>
            <li>Team-reviewable</li>
          </ul>
        </div>

        <div className="fit-aperture-caption">
          <Database aria-hidden="true" />
          <span>
            <small>THE DATABASE STORES ATTRIBUTES</small>
            <strong>Your experts recognize the commercial pattern.</strong>
          </span>
        </div>
      </div>

      <FitEnvironmentLine />
    </section>
  )
}

const fitCommonViewHeading =
  'Keep the expertise. Make the market reviewable.'

const fitCommonViewSummary =
  'Wave99 connects the accounts your experts recognize with current evidence others can inspect—one commercial view without flattening specialist judgment into a category export.'

function FitCommonViewHeader({ titleId }: { titleId: string }) {
  return (
    <header className="fit-recognition-header fit-common-lab-header">
      <small>EXPERTISE, MADE REVIEWABLE</small>
      <h3 aria-label={fitCommonViewHeading} id={titleId}>
        <span>Keep the expertise.</span>
        <strong>Make the market reviewable.</strong>
      </h3>
      <p>{fitCommonViewSummary}</p>
    </header>
  )
}

function FitCommonViewClaim({
  kind,
}: {
  kind: 'expert' | 'evidence'
}) {
  const isExpert = kind === 'expert'
  const Icon = isExpert ? Telescope : FileCheck2

  return (
    <article className={`fit-common-claim fit-common-claim-${kind}`}>
      <span>
        <Icon aria-hidden="true" />
        {isExpert ? 'EXPERT JUDGMENT' : 'VERIFIABLE EVIDENCE'}
      </span>
      <strong>
        {isExpert
          ? 'Accounts your experts recognize'
          : 'Evidence the team can inspect'}
      </strong>
      <p>
        {isExpert
          ? 'Specialist language · roles · routes'
          : 'Current change · source · proof boundary'}
      </p>
    </article>
  )
}

function FitCommonViewResult({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`fit-common-result${compact ? ' fit-common-result-compact' : ''}`}>
      <small>COMMON MARKET</small>
      <strong>One reviewable market</strong>
      <p>Expert-led. Evidence-linked. Ready to stand behind.</p>
    </div>
  )
}

function moveCommonViewGlow(event: ReactPointerEvent<HTMLDivElement>) {
  const rect = event.currentTarget.getBoundingClientRect()
  event.currentTarget.style.setProperty(
    '--common-view-glow-x',
    `${event.clientX - rect.left}px`,
  )
  event.currentTarget.style.setProperty(
    '--common-view-glow-y',
    `${event.clientY - rect.top}px`,
  )
}

function FitCommonViewOutput({
  variant,
}: {
  variant: FitCommonViewConcept
}) {
  const model = variant.replace('fit-common-', '')
  const titleId = `${variant}-title`
  const circleMechanic =
    model === 'circles'
      ? 'control'
      : model.startsWith('circles-')
        ? model.replace('circles-', '')
        : null

  if (circleMechanic) {
    return (
      <section
        aria-labelledby={titleId}
        className={`market-fit-recognition market-fit-common-lab market-fit-common-circles market-fit-common-circles-${circleMechanic} group relative isolate`}
        data-circle-mechanic={circleMechanic}
        data-fit-common-view={model}
      >
        <FitCommonViewHeader titleId={titleId} />

        <div
          className={`fit-common-circles-stage fit-common-circles-stage-${circleMechanic}`}
          data-focus-center-state={
            circleMechanic === 'focus' ? 'shared-view' : undefined
          }
          onPointerMove={moveCommonViewGlow}
        >
          <div className="fit-common-circles-plane fit-common-circles-expert">
            <span><Telescope aria-hidden="true" />EXPERT VIEW</span>
            <strong>Accounts your experts recognize</strong>
            <p>Specialist language · roles · routes</p>
          </div>

          <div className="fit-common-circles-plane fit-common-circles-evidence">
            <span><FileCheck2 aria-hidden="true" />VERIFIABLE VIEW</span>
            <strong>Evidence the team can inspect</strong>
            <p>Current change · source · proof boundary</p>
          </div>

          <div aria-hidden="true" className="fit-common-circles-signal">
            <span />
            <span />
            <span />
          </div>

          <div className="fit-common-circles-core">
            <Combine aria-hidden="true" />
            <span>
              <small>THE COMMON VIEW</small>
              <strong>One reviewable market</strong>
              <p>Judgment and evidence stay attached.</p>
            </span>
          </div>
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-common-margin') {
    return (
      <section
        aria-labelledby={titleId}
        className="market-fit-recognition market-fit-common-lab market-fit-common-margin group relative isolate"
        data-fit-common-view={model}
      >
        <FitCommonViewHeader titleId={titleId} />

        <div className="fit-common-margin-stage">
          <div aria-hidden="true" className="fit-common-margin-guide fit-common-margin-guide-left" />
          <FitCommonViewClaim kind="expert" />
          <div className="fit-common-margin-sheet">
            <span>COMMERCIAL MARKET / REVIEW COPY</span>
            <FitCommonViewResult />
            <ul aria-label="What remains visible in the common market">
              <li>Who belongs</li>
              <li>What changed</li>
              <li>What can be supported</li>
            </ul>
          </div>
          <FitCommonViewClaim kind="evidence" />
          <div aria-hidden="true" className="fit-common-margin-guide fit-common-margin-guide-right" />
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-common-fold') {
    return (
      <section
        aria-labelledby={titleId}
        className="market-fit-recognition market-fit-common-lab market-fit-common-fold group relative isolate"
        data-fit-common-view={model}
      >
        <FitCommonViewHeader titleId={titleId} />

        <div className="fit-common-fold-stage">
          <div className="fit-common-fold-sheet">
            <div className="fit-common-fold-leaf fit-common-fold-expert">
              <FitCommonViewClaim kind="expert" />
            </div>
            <div aria-hidden="true" className="fit-common-fold-spine">
              <Combine />
            </div>
            <div className="fit-common-fold-leaf fit-common-fold-evidence">
              <FitCommonViewClaim kind="evidence" />
            </div>
          </div>
          <FitCommonViewResult compact />
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-common-prism') {
    return (
      <section
        aria-labelledby={titleId}
        className="market-fit-recognition market-fit-common-lab market-fit-common-prism group relative isolate"
        data-fit-common-view={model}
      >
        <FitCommonViewHeader titleId={titleId} />

        <div className="fit-common-prism-stage">
          <div className="fit-common-prism-inputs">
            <FitCommonViewClaim kind="expert" />
            <FitCommonViewClaim kind="evidence" />
          </div>
          <div aria-hidden="true" className="fit-common-prism-beams">
            <i />
            <i />
          </div>
          <div aria-hidden="true" className="fit-common-prism-core">
            <Combine />
          </div>
          <div aria-hidden="true" className="fit-common-prism-output-beam" />
          <FitCommonViewResult />
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  if (variant === 'fit-common-register') {
    return (
      <section
        aria-labelledby={titleId}
        className="market-fit-recognition market-fit-common-lab market-fit-common-register group relative isolate"
        data-fit-common-view={model}
      >
        <FitCommonViewHeader titleId={titleId} />

        <div className="fit-common-register-stage">
          <div className="fit-common-register-sheet fit-common-register-expert">
            <i aria-hidden="true" />
            <FitCommonViewClaim kind="expert" />
          </div>
          <div className="fit-common-register-sheet fit-common-register-evidence">
            <i aria-hidden="true" />
            <FitCommonViewClaim kind="evidence" />
          </div>
          <div className="fit-common-register-result">
            <FitCommonViewResult compact />
          </div>
          <div aria-hidden="true" className="fit-common-register-mark fit-common-register-mark-a" />
          <div aria-hidden="true" className="fit-common-register-mark fit-common-register-mark-b" />
        </div>

        <FitEnvironmentLine />
      </section>
    )
  }

  return (
    <section
      aria-labelledby={titleId}
      className="market-fit-recognition market-fit-common-lab market-fit-common-rail group relative isolate"
      data-fit-common-view={model}
    >
      <FitCommonViewHeader titleId={titleId} />

      <div className="fit-common-rail-stage">
        <FitCommonViewClaim kind="expert" />
        <div aria-hidden="true" className="fit-common-rail-track">
          <i />
          <b />
          <i />
        </div>
        <FitCommonViewResult />
        <FitCommonViewClaim kind="evidence" />
      </div>

      <FitEnvironmentLine />
    </section>
  )
}

function Brand({ inverted = false }: { inverted?: boolean }) {
  return (
    <a
      aria-label="Wave99 home"
      className={`inline-flex items-center gap-2.5 rounded-md font-display text-[20px] font-extrabold tracking-[-0.055em] focus-visible:outline-2 focus-visible:outline-offset-4 ${
        inverted ? 'text-white focus-visible:outline-white' : 'text-[#111] focus-visible:outline-[#111]'
      }`}
      href="#top"
    >
      <img alt="" className="size-[27px]" height="27" src="/wave99-mark.png" width="27" />
      <span>WAVE99</span>
    </a>
  )
}

export type ReviewSource = 'header' | 'hero' | 'campaign' | 'final' | 'relay'

export function ReviewDialog({
  open,
  onClose,
  source,
  initialEmail,
}: {
  open: boolean
  onClose: () => void
  source: ReviewSource
  initialEmail: string
}) {
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!open) setSubmitted(false)
  }, [open])

  return (
    <Dialog className="relative z-[100]" onClose={onClose} open={open}>
      <DialogBackdrop className="fixed inset-0 bg-[#111]/55 backdrop-blur-md transition data-closed:opacity-0" />
      <div className="fixed inset-0 overflow-y-auto p-3 sm:p-6">
        <div className="flex min-h-full items-center justify-center">
          <DialogPanel className="review-dialog-panel w-full max-w-[940px] overflow-hidden rounded-[16px] bg-[#f6f5f3] shadow-[0_42px_120px_rgba(0,0,0,.35)] transition duration-300 data-closed:translate-y-3 data-closed:opacity-0">
            <div className="grid min-h-[620px] lg:grid-cols-[.88fr_1.12fr]">
              <div className="relative overflow-hidden bg-[#15102f] p-8 text-white sm:p-10">
                <div aria-hidden="true" className="review-dialog-aura" />
                <div className="relative z-10 flex h-full flex-col">
                  <Brand inverted />
                  <p className="mt-20 text-[12px] font-bold uppercase tracking-[0.16em] text-[#baf3de]">A focused market review</p>
                  <h2 className="mt-5 max-w-[11ch] font-display text-[clamp(2.3rem,5vw,4.2rem)] font-black uppercase leading-[.84] tracking-[-0.06em]">
                    Show us the market.
                  </h2>
                  <p className="mt-7 max-w-sm text-[16px] leading-7 text-white/65">
                    We will map the likely account universe, evidence sources, operating boundaries, and seller handoff before discussing volume.
                  </p>
                  <div className="mt-auto grid gap-3 pt-10 text-sm text-white/75">
                    {['Market reconstruction', 'Source and claim boundaries', 'Operated outreach route', 'Qualified handoff design'].map((item) => (
                      <p className="flex items-center gap-3" key={item}><Check className="size-4 text-[#baf3de]" />{item}</p>
                    ))}
                  </div>
                </div>
              </div>
              <div className="relative p-7 sm:p-10 lg:p-12">
                <button
                  aria-label="Close market review"
                  className="absolute right-5 top-5 inline-flex size-11 items-center justify-center rounded-full border border-[#111]/10 bg-white transition hover:rotate-3 hover:bg-[#efede9] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#111]"
                  onClick={onClose}
                  type="button"
                >
                  <X className="size-5" />
                </button>
                {submitted ? (
                  <div className="flex h-full flex-col justify-center" role="status">
                    <span className="inline-flex size-14 items-center justify-center rounded-full bg-[#c9f4e5] text-[#164c4d]"><CircleCheck className="size-7" /></span>
                    <DialogTitle className="mt-7 font-display text-[40px] font-semibold leading-[1.02] tracking-[-0.05em]">
                      The review path is ready.
                    </DialogTitle>
                    <p className="mt-5 max-w-md text-[17px] leading-7 text-[#111]/60">
                      This 4182 prototype deliberately does not send data. In production, this state will hand off to the approved Wave99 qualification endpoint.
                    </p>
                    <button className={`${buttonPrimary} mt-8 w-fit`} onClick={onClose} type="button">Return to the page <ArrowRight className="size-4" /></button>
                  </div>
                ) : (
                  <>
                    <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#5a496f]">Representative request flow</p>
                    <DialogTitle className="mt-4 max-w-md font-display text-[38px] font-semibold leading-[1.04] tracking-[-0.05em]">
                      What makes your market hard to read?
                    </DialogTitle>
                    <p className="mt-4 max-w-md text-[15px] leading-6 text-[#111]/55">
                      No live request is sent from this review build.
                    </p>
                    <form
                      className="mt-9 grid gap-5"
                      data-source={source}
                      onSubmit={(event) => {
                        event.preventDefault()
                        setSubmitted(true)
                      }}
                    >
                      <label className="grid gap-2 text-[13px] font-semibold">
                        Work email
                        <input
                          className="min-h-12 rounded-[8px] border border-[#111]/15 bg-white px-4 text-[16px] font-normal outline-none transition focus:border-[#5a496f] focus:ring-4 focus:ring-[#5a496f]/10"
                          defaultValue={initialEmail}
                          placeholder="you@company.com"
                          required
                          type="email"
                        />
                      </label>
                      <label className="grid gap-2 text-[13px] font-semibold">
                        Market or category
                        <input
                          className="min-h-12 rounded-[8px] border border-[#111]/15 bg-white px-4 text-[16px] font-normal outline-none transition focus:border-[#5a496f] focus:ring-4 focus:ring-[#5a496f]/10"
                          placeholder="e.g. aseptic processing equipment"
                          required
                        />
                      </label>
                      <label className="grid gap-2 text-[13px] font-semibold">
                        Where does normal outbound break?
                        <textarea
                          className="min-h-28 resize-y rounded-[8px] border border-[#111]/15 bg-white p-4 text-[16px] font-normal leading-6 outline-none transition focus:border-[#5a496f] focus:ring-4 focus:ring-[#5a496f]/10"
                          placeholder="The accounts exist, but..."
                          required
                        />
                      </label>
                      <button className={`${buttonPrimary} mt-1 min-h-12 w-full`} type="submit">Prepare the market review <ArrowUpRight className="size-4" /></button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

const productMenu = [
  ['Market reconstruction', 'Build the account universe the CRM cannot expose.', Radar],
  ['Outreach production', 'Turn source evidence into a reason to talk.', Workflow],
  ['Sending operations', 'Operate health, timing, limits, and suppression.', ShieldCheck],
  ['Reply intelligence', 'Triage useful replies and prepare the handoff.', Inbox],
] as const

function HeaderPopover({
  label,
  children,
  compact = false,
}: {
  label: string
  children: ReactNode
  compact?: boolean
}) {
  return (
    <Popover className="relative">
      <PopoverButton className="group inline-flex min-h-11 items-center gap-1.5 rounded-md px-1 text-[14px] font-medium text-[#111] outline-none transition hover:text-[#5a496f] focus-visible:ring-2 focus-visible:ring-[#111]">
        {label}<ChevronDown className="size-3.5 transition group-data-open:rotate-180" />
      </PopoverButton>
      <PopoverPanel
        anchor="bottom start"
        className={`z-50 mt-1 rounded-[12px] border border-white/70 bg-white/90 p-4 shadow-[0_28px_70px_rgba(17,17,17,.15)] backdrop-blur-2xl transition duration-200 [--anchor-gap:8px] data-closed:-translate-y-2 data-closed:opacity-0 ${
          compact ? 'w-[360px]' : 'w-[600px]'
        }`}
      >
        {children}
      </PopoverPanel>
    </Popover>
  )
}

export function SiteHeader({ onReview }: { onReview: (source: ReviewSource) => void }) {
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const updateHeader = () => setScrolled(window.scrollY > 54)
    updateHeader()
    window.addEventListener('scroll', updateHeader, { passive: true })
    return () => window.removeEventListener('scroll', updateHeader)
  }, [])

  return (
    <header className={`site-header inset-x-0 top-0 z-40${scrolled ? ' is-scrolled' : ''}`}>
      <nav
        aria-label="Primary navigation"
        className="header-nav-shell mx-auto flex h-20 w-full max-w-[1440px] items-center px-5 sm:px-8 lg:px-14"
      >
        <Brand />
        <PopoverGroup className="ml-[105px] hidden items-center gap-10 lg:flex">
          <HeaderPopover label="Product">
            <div className="grid grid-cols-[1.05fr_.95fr] gap-4">
              <div className="grid gap-1">
                {productMenu.map(([title, detail, Icon], index) => (
                  <a className={`mega-link ${index === 0 ? 'is-featured' : ''}`} href={`#${index === 0 ? 'platform' : 'how-it-works'}`} key={title}>
                    <span><Icon /></span>
                    <span><strong>{title}</strong><small>{detail}</small></span>
                    <ArrowUpRight />
                  </a>
                ))}
              </div>
              <a className="mega-feature" href="#portal">
                <p>THE WAVE99 PORTAL</p>
                <h3>Inspect the operation without having to run it.</h3>
                <div className="mega-mini-portal">
                  <span /><span /><span />
                  <i /><i /><i />
                </div>
                <b>See the operating layer <ArrowRight /></b>
              </a>
            </div>
          </HeaderPopover>
          <HeaderPopover compact label="Why us">
            <div className="grid gap-2">
              {[
                ['For hard-to-read markets', 'Where commercial truth is scattered beyond the CRM.'],
                ['Done-for-you operation', 'Your team sets direction; Wave99 runs the motion.'],
                ['Evidence stays attached', 'Source, thesis, thread, owner, and next step travel together.'],
              ].map(([title, detail]) => (
                <a className="rounded-[9px] p-4 transition hover:bg-[#f1efeb]" href="#why-wave99" key={title}>
                  <strong className="block text-[14px]">{title}</strong>
                  <small className="mt-1 block text-[12px] leading-5 text-[#111]/50">{detail}</small>
                </a>
              ))}
            </div>
          </HeaderPopover>
          <HeaderPopover compact label="Resources">
            <div className="grid grid-cols-2 gap-2">
              {['Operating method', 'Market notes', 'Evidence standards', 'Portal walkthrough'].map((item) => (
                <a className="rounded-[9px] p-4 text-[14px] font-medium transition hover:bg-[#f1efeb]" href="#insights" key={item}>{item}</a>
              ))}
            </div>
          </HeaderPopover>
          <a className="inline-flex min-h-11 items-center rounded-md text-[14px] font-medium transition hover:text-[#5a496f] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#111]" href="#markets">Markets</a>
          <a className="inline-flex min-h-11 items-center rounded-md text-[14px] font-medium transition hover:text-[#5a496f] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#111]" href="#how-it-works">Method</a>
        </PopoverGroup>
        <div className="ml-auto flex items-center gap-2.5">
          <a className="hidden min-h-[39px] items-center rounded-[8px] border border-[#111]/15 bg-white/45 px-4 text-[14px] font-medium transition hover:bg-white sm:inline-flex" href="https://app.wave99.com">Open portal</a>
          <button aria-label="Review your market" className={`${buttonPrimary} whitespace-nowrap px-4 max-sm:px-4`} onClick={() => onReview('header')} type="button">
            <span className="hidden sm:inline">Review market</span>
            <span aria-hidden="true" className="sm:hidden">Review market</span>
          </button>
        </div>
      </nav>
    </header>
  )
}

export function heroProductDemoScaleForProgress(progress: number) {
  const clamped = Math.max(0, Math.min(1, progress))
  const smooth = (value: number) => value * value * (3 - 2 * value)

  if (clamped < 0.38) {
    return 0.86 + smooth(clamped / 0.38) * 0.14
  }

  if (clamped <= 0.64) return 1

  return 1 - smooth((clamped - 0.64) / 0.36) * 0.1
}

function HeroProductScrollZoom({ children }: { children: ReactNode }) {
  const frameRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    offset: ['start end', 'end start'],
    target: frameRef,
    trackContentSize: true,
  })
  const scale = useTransform(scrollYProgress, heroProductDemoScaleForProgress)

  return (
    <m.div
      className="hero-product-scroll-zoom"
      data-scroll-engine="motion-use-scroll-transform"
      data-scroll-scale="0.86-1.00-0.90"
      data-scroll-zoom="product-demo-in-out"
      ref={frameRef}
      style={{ scale }}
    >
      {children}
    </m.div>
  )
}

export function Hero({
  onReview,
  setEmail,
  email,
  demoScrollZoom = 'control',
}: {
  onReview: (source: ReviewSource) => void
  setEmail: (email: string) => void
  email: string
  demoScrollZoom?: 'control' | 'in-out'
}) {
  const reducedMotion = useReducedMotion()
  const [ambientPaused, setAmbientPaused] = useState(false)

  return (
    <section
      className="hero-section hero-mainframe"
      data-ambient-paused={ambientPaused ? 'true' : 'false'}
      data-product-demo-motion={demoScrollZoom}
      id="top"
    >
      <div className="hero-ambient hero-ambient-live">
        <HeroShader paused={ambientPaused} />
        <i className="ambient-grain" />
      </div>
      <m.div
        animate={reducedMotion ? undefined : { opacity: 1, scale: 1, y: 0 }}
        className="relative z-10 mx-auto max-w-[700px] px-5 text-center"
        initial={reducedMotion ? false : { opacity: 0, scale: 0.985, y: 18 }}
        transition={{ duration: 0.72, ease: [0, 0, 0.3, 1] }}
      >
        <a className="announcement-pill" href="#platform">
          <span>NEW</span>
          Outdated B2B markets, made sellable
          <ArrowRight />
        </a>
        <h1>
          Niche markets to pipeline:{' '}
          <span className="hero-iridescent-ink">Human + AI</span>
        </h1>
        <p className="hero-lead">
          Wave99 reconstructs the market and returns qualified conversations with the evidence attached.
        </p>
        <form
          className="hero-form"
          onSubmit={(event) => {
            event.preventDefault()
            onReview('hero')
          }}
        >
          <label className="sr-only" htmlFor="hero-email">Company email</label>
          <input id="hero-email" onChange={(event) => setEmail(event.target.value)} placeholder="Enter your company email" type="email" value={email} />
          <button type="submit">Review your market</button>
        </form>
        <div className="hero-assurance">
          <span><Check /> Evidence attached</span>
          <span><Check /> Operated by Wave99</span>
        </div>
        {!reducedMotion ? (
          <button
            aria-label={ambientPaused ? 'Resume ambient gradient motion' : 'Pause ambient gradient motion'}
            aria-pressed={ambientPaused}
            className="hero-motion-toggle"
            onClick={() => setAmbientPaused((current) => !current)}
            type="button"
          >
            {ambientPaused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />}
            <span>{ambientPaused ? 'Resume ambient motion' : 'Pause ambient motion'}</span>
          </button>
        ) : (
          <span className="hero-motion-reduced">Motion reduced</span>
        )}
      </m.div>
      {(() => {
        const portal = (
          <m.div
            animate={reducedMotion ? undefined : { opacity: 1, scale: 1, y: 0 }}
            className="hero-mainframe-portal"
            id="portal"
            initial={reducedMotion ? false : { opacity: 0, scale: 0.965, y: 34 }}
            transition={{ delay: 0.24, duration: 0.86, ease: [0, 0, 0.3, 1] }}
          >
            <FittedPortalCanvas fitMode="width">
              <div className="portal-authored-frame hero-premium-stage">
                <div aria-hidden="true" className="hero-stage-bloom" />
                <div aria-hidden="true" className="hero-stage-sheen" />
                <PortalPreview />
                <HeroStageSignals />
              </div>
            </FittedPortalCanvas>
            <a className="portal-chapter-cue" href="#markets">
              See what changes <ArrowRight aria-hidden="true" />
            </a>
          </m.div>
        )

        if (demoScrollZoom !== 'in-out') return portal

        if (reducedMotion) {
          return (
            <div
              className="hero-product-scroll-zoom is-reduced"
              data-scroll-engine="reduced-motion-static"
              data-scroll-scale="1.00"
              data-scroll-zoom="reduced"
            >
              {portal}
            </div>
          )
        }

        return <HeroProductScrollZoom>{portal}</HeroProductScrollZoom>
      })()}
    </section>
  )
}

export function ResultsFirstSection({
  headingPlacement = 'section',
  introAlignment = 'control',
  onReview,
  relayConcept: relayConceptOverride,
  surface = 'light',
  stopAfterOutcome = false,
}: {
  headingPlacement?: 'section' | 'stage'
  introAlignment?: 'control' | 'centered'
  onReview: (source: ReviewSource) => void
  relayConcept?: RelayConcept | 'control'
  surface?: 'light' | 'dark'
  stopAfterOutcome?: boolean
}) {
  const relayConcept = relayConceptOverride ?? getRelayConcept()
  const usesFitDiagnostic = relayConcept === 'fit-diagnostic'
  const fitLabVariant = fitLabConcepts.includes(relayConcept as FitLabConcept)
    ? relayConcept as FitLabConcept
    : null
  const fitMetaphorVariant = fitMetaphorConcepts.includes(
    relayConcept as FitMetaphorConcept,
  )
    ? relayConcept as FitMetaphorConcept
    : null
  const fitRecognitionVariant = fitRecognitionConcepts.includes(
    relayConcept as FitRecognitionConcept,
  )
    ? relayConcept as FitRecognitionConcept
    : null
  const fitCommonViewVariant = fitCommonViewConcepts.includes(
    relayConcept as FitCommonViewConcept,
  )
    ? relayConcept as FitCommonViewConcept
    : null
  const usesFitCandidate =
    usesFitDiagnostic
    || fitLabVariant !== null
    || fitMetaphorVariant !== null
    || fitRecognitionVariant !== null
    || fitCommonViewVariant !== null
  const usesOutcomeLed = relayConcept === 'outcome-led' || usesFitCandidate
  const handoffLabVariant = handoffLabConcepts.includes(
    relayConcept as HandoffLabConcept,
  )
    ? relayConcept as HandoffLabConcept
    : null
  const usesGuidedLensMechanics =
    relayConcept === 'guided-lenses'
    || relayConcept === 'free-lenses'
    || relayConcept === 'relay-lenses'
    || relayConcept === 'vertical-polished'
    || relayConcept === 'horizontal-polished'
    || relayConcept === 'distinct-evidence'
    || relayConcept === 'handoff-docket'
    || relayConcept === 'handoff-folio'
    || relayConcept === 'handoff-whole'
    || usesOutcomeLed
    || handoffLabVariant !== null
  const isDirectReviewCandidate =
    relayConcept === 'free-lenses'
    || relayConcept === 'relay-lenses'
    || relayConcept === 'vertical-polished'
    || relayConcept === 'horizontal-polished'
    || relayConcept === 'distinct-evidence'
    || relayConcept === 'handoff-docket'
    || relayConcept === 'handoff-folio'
    || relayConcept === 'handoff-whole'
    || usesOutcomeLed
    || handoffLabVariant !== null
  const usesHorizontalEvidenceRelay = relayConcept === 'horizontal-polished'
  const usesDistinctEvidence =
    relayConcept === 'distinct-evidence'
    || relayConcept === 'handoff-docket'
    || relayConcept === 'handoff-folio'
    || relayConcept === 'handoff-whole'
    || usesOutcomeLed
    || handoffLabVariant !== null
  const usesHandoffDocket =
    relayConcept === 'handoff-docket'
    || relayConcept === 'handoff-folio'
    || relayConcept === 'handoff-whole'
    || handoffLabVariant !== null
  const usesHandoffFolio =
    relayConcept === 'handoff-folio'
    || relayConcept === 'handoff-whole'
    || handoffLabVariant !== null
  const usesHandoffWhole = relayConcept === 'handoff-whole'
  const guidedLayout =
    relayConcept === 'vertical-polished'
      ? 'portrait-deck'
      : relayConcept === 'free-lenses'
        || relayConcept === 'relay-lenses'
        || relayConcept === 'horizontal-polished'
        || relayConcept === 'distinct-evidence'
        || relayConcept === 'handoff-docket'
        || relayConcept === 'handoff-folio'
        || relayConcept === 'handoff-whole'
        || usesOutcomeLed
        || handoffLabVariant !== null
        ? 'horizontal-rows'
        : undefined
  const [activeGuidedLens, setActiveGuidedLens] = useState<string | null>(null)
  const relayConceptClasses =
    relayConcept === 'open-storyboard'
      ? 'relay-concept-storyboard relay-concept-open-storyboard'
      : relayConcept === 'free-lenses'
        ? 'relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses'
        : relayConcept === 'relay-lenses'
          ? 'relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-relay-lenses'
          : relayConcept === 'vertical-polished'
            ? 'relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-vertical-polished'
            : relayConcept === 'horizontal-polished'
              ? 'relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished'
              : relayConcept === 'distinct-evidence'
                ? 'relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished relay-concept-distinct-evidence'
              : fitMetaphorVariant
                ? `relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished relay-concept-distinct-evidence relay-concept-outcome-led relay-concept-fit-metaphor relay-concept-${fitMetaphorVariant}`
              : fitCommonViewVariant
                ? `relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished relay-concept-distinct-evidence relay-concept-outcome-led relay-concept-fit-recognition relay-concept-fit-common-lab relay-concept-${fitCommonViewVariant}`
              : fitRecognitionVariant
                ? `relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished relay-concept-distinct-evidence relay-concept-outcome-led relay-concept-fit-recognition relay-concept-${fitRecognitionVariant}`
              : fitLabVariant
                ? `relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished relay-concept-distinct-evidence relay-concept-outcome-led relay-concept-fit-lab relay-concept-${fitLabVariant}`
                : usesFitDiagnostic
                  ? 'relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished relay-concept-distinct-evidence relay-concept-outcome-led relay-concept-fit-diagnostic'
                : relayConcept === 'outcome-led'
                  ? 'relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished relay-concept-distinct-evidence relay-concept-outcome-led'
                : relayConcept === 'handoff-docket'
                  ? 'relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished relay-concept-distinct-evidence relay-concept-handoff-docket'
                : relayConcept === 'handoff-folio'
                  ? 'relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished relay-concept-distinct-evidence relay-concept-handoff-docket relay-concept-handoff-folio'
                : relayConcept === 'handoff-whole'
                  ? 'relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished relay-concept-distinct-evidence relay-concept-handoff-docket relay-concept-handoff-folio relay-concept-handoff-whole'
                : handoffLabVariant
                  ? `relay-concept-evidence-lenses relay-concept-guided-lenses relay-concept-free-lenses relay-concept-horizontal-polished relay-concept-distinct-evidence relay-concept-handoff-docket relay-concept-handoff-folio relay-concept-handoff-lab relay-concept-${handoffLabVariant}`
              : usesGuidedLensMechanics
                ? 'relay-concept-evidence-lenses relay-concept-guided-lenses'
                : `relay-concept-${relayConcept}`

  const moveGloss = (event: ReactPointerEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    event.currentTarget.style.setProperty('--result-gloss-x', `${event.clientX - rect.left}px`)
    event.currentTarget.style.setProperty('--result-gloss-y', `${event.clientY - rect.top}px`)
  }

  const moveLensGloss = (event: ReactPointerEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    event.currentTarget.style.setProperty('--lens-gloss-x', `${event.clientX - rect.left}px`)
    event.currentTarget.style.setProperty('--lens-gloss-y', `${event.clientY - rect.top}px`)
  }

  const marketIcons = [Factory, HardHat, FileSearch, ShieldCheck, Network, Database]

  return (
    <section
      className={`results-first-section ${relayConceptClasses}${surface === 'dark' ? ' results-sector-dark' : ''}`}
      data-card-surface={surface === 'dark' ? 'opaque-on-dark' : 'translucent-on-light'}
      data-headline-placement={headingPlacement}
      data-intro-alignment={introAlignment}
      data-proof-standard={stopAfterOutcome ? 'omitted' : 'shown'}
      data-relay-concept={relayConcept}
      data-sector-theme={surface}
      id="markets"
    >
      {headingPlacement === 'section' ? (
        <PremiumReveal className="results-first-heading">
          <p><Sparkles /> WHAT YOUR TEAM GETS</p>
          <h2>
            <span>The market was always there.</span>
            <span className="results-action-line">
              Now <em>your team</em> can act on it.
            </span>
          </h2>
          <strong>
            Wave99 turns outdated categories and scattered evidence into a market
            your team can review, prioritize, and convert into useful conversations.
          </strong>
        </PremiumReveal>
      ) : null}

      <div className="operational-change-stage" onPointerMove={moveGloss}>
        <header className="operational-change-intro">
          {usesOutcomeLed ? (
            <div className="operational-change-lockup operational-change-outcome-lockup">
              <p>THE OPERATING SEQUENCE</p>
              {headingPlacement === 'stage' ? (
                <h3
                  aria-label="The market was always there. Now your team can act on it."
                  className={`results-stage-headline${introAlignment === 'centered' ? ' results-stage-headline-centered' : ''}`}
                  data-headline-rows="3"
                  data-type-rhythm={introAlignment === 'centered' ? 'wave99-centered-chapter-dm-sans' : undefined}
                >
                  <span>The market was always there.</span>
                  <span className="results-stage-action-line">
                    Now <em>your team</em>
                  </span>
                  <span>can act on it.</span>
                </h3>
              ) : (
                <h3>
                  Three decisions become one{' '}
                  <strong>seller-ready conversation.</strong>
                </h3>
              )}
            </div>
          ) : (
            <div className="operational-change-lockup">
              <p>THE OPERATING SEQUENCE</p>
              <h3
                aria-label={
                  isDirectReviewCandidate
                    ? 'Three decisions become one qualified conversation'
                    : 'Three decisions become one seller-ready moment'
                }
              >
                <span><b>03</b><span>decisions</span></span>
                <ArrowRight aria-hidden="true" />
                <span>
                  <b>01</b>
                  <span>{isDirectReviewCandidate ? 'qualified conversation' : 'seller-ready moment'}</span>
                </span>
              </h3>
            </div>
          )}
          <span>
            {usesOutcomeLed
              ? 'Define who belongs. Read why now. Carry source, reason, and owner into the first call.'
              : 'Define who belongs. Read when it matters. Carry the original reason into the first call.'}
          </span>
          {isDirectReviewCandidate ? (
            <button
              className="operational-change-direct-review"
              onClick={() => onReview('relay')}
              type="button"
            >
              Review your market <ArrowRight aria-hidden="true" />
            </button>
          ) : (
            <a href="#final-cta">Review your market <ArrowRight aria-hidden="true" /></a>
          )}
        </header>

        <div
          className="operational-change-relay"
          data-guided-active={usesGuidedLensMechanics ? activeGuidedLens ?? 'none' : undefined}
          data-guided-layout={guidedLayout}
          onPointerMove={usesGuidedLensMechanics ? moveLensGloss : undefined}
        >
          {outcomeStandards.map((outcome, index) => {
            const MomentIcon = [Database, Radar, Inbox][index]

            return (
            <article
              className={`operational-change-moment moment-${outcome.id}`}
              data-guided-lens={usesGuidedLensMechanics ? outcome.id : undefined}
              key={outcome.id}
              onBlur={() => {
                if (usesGuidedLensMechanics) setActiveGuidedLens(null)
              }}
              onClick={() => {
                if (usesGuidedLensMechanics) setActiveGuidedLens(outcome.id)
              }}
              onFocus={() => {
                if (usesGuidedLensMechanics) setActiveGuidedLens(outcome.id)
              }}
              onPointerEnter={(event) => {
                if (usesGuidedLensMechanics && event.pointerType !== 'touch') {
                  setActiveGuidedLens(outcome.id)
                }
              }}
              onPointerLeave={(event) => {
                if (
                  usesGuidedLensMechanics
                  && event.pointerType !== 'touch'
                  && document.activeElement !== event.currentTarget
                ) {
                  setActiveGuidedLens(null)
                }
              }}
              tabIndex={usesGuidedLensMechanics ? 0 : undefined}
            >
              <header>
                <span>{outcome.number}</span>
                <p>{outcome.kicker}</p>
                <MomentIcon aria-hidden="true" />
              </header>
              <div className="operational-change-copy">
                <h4>{outcome.title}</h4>
                <p>
                  {usesOutcomeLed && outcome.id === 'context'
                    ? 'Source, buyer role, why-now, and accountable owner reach the seller together before the first call.'
                    : outcome.body}
                </p>
              </div>

              {outcome.id === 'market' ? (
                <div
                  className={`operational-market-visual${
                    usesHorizontalEvidenceRelay ? ' operational-evidence-horizontal' : ''
                  }${
                    usesDistinctEvidence
                      ? ' operational-evidence-distinct distinct-market-boundary'
                      : ''
                  }`}
                >
                  {usesDistinctEvidence ? (
                    <div className="distinct-boundary-map">
                      <div className="distinct-boundary-fragments">
                        <span className="distinct-boundary-source">
                          <Database aria-hidden="true" />
                          Database category
                        </span>
                        <div>
                          {outcome.markers.map((marker) => <span key={marker}>{marker}</span>)}
                          <span className="distinct-boundary-exclusion">Exclusions</span>
                        </div>
                      </div>
                      <div className="distinct-boundary-core">
                        <ShieldCheck aria-hidden="true" />
                        <span>
                          <small>Defined boundary</small>
                          <strong>Reviewable market</strong>
                        </span>
                      </div>
                    </div>
                  ) : usesHorizontalEvidenceRelay ? (
                    <>
                      <small>Database → market</small>
                      <div className="operational-evidence-path">
                        <div className="operational-evidence-source">
                          <span className="operational-evidence-source-title">
                            <Database aria-hidden="true" />
                            Database category
                          </span>
                          <footer>
                            {outcome.markers.map((marker) => <span key={marker}>{marker}</span>)}
                            <span>Exclusions</span>
                          </footer>
                        </div>
                        <ArrowRight className="operational-evidence-arrow" aria-hidden="true" />
                        <div className="operational-evidence-result">
                          <ShieldCheck aria-hidden="true" />
                          <span>
                            <strong>Reviewable market</strong>
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <small>{outcome.visualLabel}</small>
                      <div>
                        <span><Database aria-hidden="true" />Category label</span>
                        <ArrowRight aria-hidden="true" />
                        <strong><ShieldCheck aria-hidden="true" />Sourced market</strong>
                      </div>
                      <p>{outcome.visualValue}</p>
                      <footer>{outcome.markers.map((marker) => <span key={marker}>{marker}</span>)}</footer>
                    </>
                  )}
                </div>
              ) : null}

              {outcome.id === 'priority' ? (
                <div
                  className={`operational-timing-visual${
                    usesHorizontalEvidenceRelay ? ' operational-evidence-horizontal' : ''
                  }${
                    usesDistinctEvidence
                      ? ' operational-evidence-distinct distinct-timing-convergence'
                      : ''
                  }`}
                >
                  {usesDistinctEvidence ? (
                    <div className="distinct-signal-convergence">
                      <div className="distinct-signal-sources">
                        {outcome.markers.map((marker) => (
                          <span key={marker}><i />{marker}</span>
                        ))}
                      </div>
                      <div aria-hidden="true" className="distinct-signal-collector">
                        <i />
                        <i />
                        <i />
                        <i />
                      </div>
                      <div className="distinct-signal-result">
                        <Radar aria-hidden="true" />
                        <span>
                          <small>Commercial timing</small>
                          <strong>Reason to act now</strong>
                        </span>
                      </div>
                    </div>
                  ) : usesHorizontalEvidenceRelay ? (
                    <>
                      <small>Signals → timing</small>
                      <div className="operational-evidence-path">
                        <div className="operational-evidence-source operational-evidence-signals">
                          {outcome.markers.map((marker) => <span key={marker}><i />{marker}</span>)}
                        </div>
                        <ArrowRight className="operational-evidence-arrow" aria-hidden="true" />
                        <div className="operational-evidence-result">
                          <Radar aria-hidden="true" />
                          <span>
                            <strong>Reason to act now</strong>
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <small>{outcome.visualLabel}</small>
                      <div>
                        {outcome.markers.map((marker) => <span key={marker}><i />{marker}</span>)}
                      </div>
                      <strong><Radar aria-hidden="true" />{outcome.visualValue}</strong>
                    </>
                  )}
                </div>
              ) : null}

              {outcome.id === 'context' ? (
                <div
                  className={`operational-context-visual${
                    usesHorizontalEvidenceRelay ? ' operational-evidence-horizontal' : ''
                  }${
                    usesDistinctEvidence
                      ? ' operational-evidence-distinct distinct-context-packet'
                      : ''
                  }`}
                >
                  {usesDistinctEvidence ? (
                    usesOutcomeLed ? (
                      <div className="distinct-context-stage outcome-led-context-stage">
                        <div
                          aria-label="What reaches the seller intact"
                          className="distinct-context-stack outcome-led-context-stack"
                        >
                          <span><Check aria-hidden="true" />Source attached</span>
                          <span><Check aria-hidden="true" />Reason preserved</span>
                          <span><Check aria-hidden="true" />Owner accountable</span>
                        </div>
                        <div aria-hidden="true" className="distinct-context-binding">
                          <i />
                        </div>
                        <div className="distinct-context-result outcome-led-context-result">
                          <Inbox aria-hidden="true" />
                          <span>
                            <small>Seller-ready</small>
                            <strong>Conversation</strong>
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="distinct-context-stage">
                        <div className="distinct-context-stack">
                          {outcome.markers.map((marker) => (
                            <span key={marker}><Check aria-hidden="true" />{marker}</span>
                          ))}
                        </div>
                        <div aria-hidden="true" className="distinct-context-binding">
                          <i />
                        </div>
                        <div className="distinct-context-result">
                          <Inbox aria-hidden="true" />
                          <span>
                            <small>Attached context</small>
                            <strong>First-call ready</strong>
                          </span>
                        </div>
                      </div>
                    )
                  ) : usesHorizontalEvidenceRelay ? (
                    <>
                      <small>Context → seller</small>
                      <div className="operational-evidence-path">
                        <div className="operational-evidence-source operational-evidence-context">
                          {outcome.markers.map((marker) => (
                            <span key={marker}><Check aria-hidden="true" />{marker}</span>
                          ))}
                        </div>
                        <ArrowRight className="operational-evidence-arrow" aria-hidden="true" />
                        <div className="operational-evidence-result">
                          <Inbox aria-hidden="true" />
                          <span>
                            <strong>First-call ready</strong>
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <small>{outcome.visualLabel}</small>
                      <div>
                        {outcome.markers.map((marker) => <span key={marker}><Check aria-hidden="true" />{marker}</span>)}
                      </div>
                      <strong><Inbox aria-hidden="true" />{outcome.visualValue}</strong>
                    </>
                  )}
                </div>
              ) : null}
            </article>
            )
          })}
        </div>

        {!usesOutcomeLed ? (
          <footer
            className={`operational-change-output${
              usesHandoffDocket ? ' operational-handoff-docket' : ''
            }${
              usesHandoffFolio ? ' operational-handoff-folio' : ''
            }${
              usesHandoffWhole ? ' operational-handoff-whole' : ''
            }${
              handoffLabVariant ? ' operational-handoff-lab' : ''
            }`}
          >
            {usesHandoffDocket ? (
              handoffLabVariant ? (
                <HandoffLabOutput variant={handoffLabVariant} />
              ) : usesHandoffFolio ? (
                <>
                  <div className="handoff-folio-title">
                    <small>THE COMMERCIAL OUTPUT</small>
                    <h3 aria-label="Qualified conversation">
                      <strong>Qualified conversation</strong>
                    </h3>
                    <p>Source, reason, and ownership arrive together.</p>
                  </div>
                  {usesHandoffWhole ? (
                    <div className="handoff-whole-output">
                      <div aria-hidden="true" className="handoff-whole-convergence">
                        <i />
                        <i />
                        <i />
                        <span><Inbox /></span>
                      </div>
                      <div className="handoff-whole-copy">
                        <small>SELLER-READY HANDOFF</small>
                        <strong>Arrives whole.</strong>
                        <ul aria-label="What arrives with the qualified conversation">
                          <li>Source attached</li>
                          <li>Reason preserved</li>
                          <li>Owner accountable</li>
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="handoff-folio-receipt">
                      <div className="handoff-folio-receipt-header">
                        <span>HANDOFF / COMPLETE</span>
                        <span><Inbox aria-hidden="true" />SELLER READY</span>
                      </div>
                      <dl
                        aria-label="What remains intact in the qualified conversation"
                        className="handoff-folio-fields"
                      >
                        <div>
                          <dt>Source</dt>
                          <dd>Evidence attached</dd>
                        </div>
                        <div>
                          <dt>Why now</dt>
                          <dd>Reason preserved</dd>
                        </div>
                        <div>
                          <dt>Owner</dt>
                          <dd>Accountable</dd>
                        </div>
                      </dl>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="handoff-docket-title">
                    <small>THE COMMERCIAL OUTPUT</small>
                    <h3 aria-label="Qualified conversation">
                      <strong>Qualified conversation</strong>
                    </h3>
                  </div>
                  <dl
                    aria-label="What remains intact in the qualified conversation"
                    className="handoff-docket-fields"
                  >
                    <div>
                      <dt>Source</dt>
                      <dd>Attached</dd>
                    </div>
                    <div>
                      <dt>Reason</dt>
                      <dd>Attached</dd>
                    </div>
                    <div>
                      <dt>Owner</dt>
                      <dd>Accountable</dd>
                    </div>
                  </dl>
                </>
              )
            ) : (
              <>
                <div>
                  <small>THE COMMERCIAL OUTPUT</small>
                  <h3 aria-label="Not another list. A qualified conversation.">
                    <span>Another list</span>
                    <ArrowRight aria-hidden="true" />
                    <strong>Qualified conversation</strong>
                  </h3>
                </div>
                <div className="operational-output-packet">
                  <p>The conversation reaches your team whole.</p>
                  <ul aria-label="What remains attached to the qualified conversation">
                    <li><Check aria-hidden="true" />Original source</li>
                    <li><Check aria-hidden="true" />Reason to talk</li>
                    <li><Check aria-hidden="true" />Owner and next step</li>
                  </ul>
                </div>
              </>
            )}
          </footer>
        ) : null}
      </div>

      {stopAfterOutcome ? null : (
        <div className="results-proof-standard">
          <ShieldCheck aria-hidden="true" />
          <p>
            <strong>No borrowed proof.</strong>
            Customer names, metrics, and quotations appear only with a source,
            measurement period, and permission.
          </p>
        </div>
      )}

      {stopAfterOutcome ? null : fitCommonViewVariant ? (
        <FitCommonViewOutput variant={fitCommonViewVariant} />
      ) : fitRecognitionVariant ? (
        <FitRecognitionOutput variant={fitRecognitionVariant} />
      ) : fitMetaphorVariant ? (
        <FitMetaphorOutput variant={fitMetaphorVariant} />
      ) : fitLabVariant ? (
        <FitLabOutput variant={fitLabVariant} />
      ) : usesFitDiagnostic ? (
        <section
          aria-labelledby="market-fit-diagnostic-title"
          className="market-fit-diagnostic"
          data-fit-model="diagnostic"
        >
          <header>
            <p>WAVE99 FITS WHEN</p>
            <h3
              aria-label="Your market is easy to describe. Difficult to query."
              id="market-fit-diagnostic-title"
            >
              <span>Your market is easy to describe.</span>
              <strong>Difficult to query.</strong>
            </h3>
            <span>
              The commercial truth exists, but it is scattered across technical
              language, projects, certifications, partners, and specialist roles.
            </span>
          </header>

          <ul className="market-fit-diagnostic-signals">
            {marketFitSignals.map((signal) => (
              <li key={signal.title}>
                <span aria-hidden="true" className="market-fit-diagnostic-marker" />
                <strong>{signal.title}</strong>
                <p>{signal.detail}</p>
              </li>
            ))}
          </ul>

          <footer>
            <span>COMMON ENVIRONMENTS</span>
            <ul aria-label="Common Wave99 market environments">
              {marketFitEnvironments.map((environment) => (
                <li key={environment}>{environment}</li>
              ))}
            </ul>
          </footer>
        </section>
      ) : (
        <div className="market-fit-strip">
          <header>
            <p>ESPECIALLY USEFUL WHEN</p>
            <h3>The market behaves like this</h3>
            <span>Recognition helps a buyer see the fit. It does not pretend to be customer proof.</span>
          </header>
          <div className="market-fit-list">
          {marketTypes.map((market, index) => {
            const Icon = marketIcons[index]
            return (
              <article key={market.name}>
                <Icon aria-hidden="true" />
                <div>
                  <strong>{market.name}</strong>
                  <p>{market.detail}</p>
                </div>
              </article>
            )
          })}
          </div>
        </div>
      )}
    </section>
  )
}

function OperatingEvidenceInterlude() {
  return (
    <aside
      aria-labelledby="operating-evidence-title"
      className="operating-evidence-interlude"
      data-proof-kind="operating-evidence"
    >
      <div className="operating-evidence-copy">
        <span><FileCheck2 aria-hidden="true" /> INSPECTABLE OPERATING PROOF</span>
        <h3 id="operating-evidence-title">The decision trail stays attached.</h3>
        <p>
          Source, commercial interpretation, message, reply, and accountable
          owner remain connected from the first market decision to seller handoff.
        </p>
      </div>
      <dl aria-label="What remains attached" className="operating-evidence-receipt">
        <div>
          <dt>Source</dt>
          <dd><FileSearch aria-hidden="true" /> Evidence attached</dd>
        </div>
        <div>
          <dt>Judgment</dt>
          <dd><BrainCircuit aria-hidden="true" /> Inference labeled</dd>
        </div>
        <div>
          <dt>Handoff</dt>
          <dd><CircleCheck aria-hidden="true" /> Owner accountable</dd>
        </div>
      </dl>
      <footer>
        <span><b>Representative operating proof</b><small>Inspectable in the Wave99 portal</small></span>
        <img alt="" src="/wave99-mark.png" />
      </footer>
    </aside>
  )
}

function DarkOperatingSection({ proofCadence }: { proofCadence: ProofCadenceConcept | 'control' }) {
  const reducedMotion = useReducedMotion()
  const operatingSignals = [
    {
      detail: 'Expansion evidence enters the account record.',
      icon: Radar,
      label: 'New plant activity',
      status: '12 account theses ready',
    },
    {
      detail: 'A changed route reveals the real buying network.',
      icon: Network,
      label: 'Distributor territory added',
      status: '7 market routes updated',
    },
    {
      detail: 'New language can signal an open specification.',
      icon: FileSearch,
      label: 'Tender language changed',
      status: '4 timing windows found',
    },
    {
      detail: 'Every message stays inside approved proof.',
      icon: ShieldCheck,
      label: 'Claims checked',
      status: '18 claims inside brief',
    },
    {
      detail: 'Channel, timing, health, and suppression align.',
      icon: Send,
      label: 'Channel route ready',
      status: '9 safe routes prepared',
    },
    {
      detail: 'The thread returns with source and next step.',
      icon: Inbox,
      label: 'Reply routed to owner',
      status: '3 handoffs seller-ready',
    },
  ] as const
  const [activeSignal, setActiveSignal] = useState(0)
  const [cyclePaused, setCyclePaused] = useState(false)
  const [interactionPaused, setInteractionPaused] = useState(false)
  const [cycleKey, setCycleKey] = useState(0)
  const rotationPaused = Boolean(reducedMotion || cyclePaused || interactionPaused)

  useEffect(() => {
    if (rotationPaused) return
    const timeout = window.setTimeout(() => {
      setActiveSignal((current) => (current + 1) % operatingSignals.length)
      setCycleKey((current) => current + 1)
    }, 7200)
    return () => window.clearTimeout(timeout)
  }, [activeSignal, cycleKey, operatingSignals.length, rotationPaused])

  const selectSignal = (index: number) => {
    setActiveSignal(index)
    setCycleKey((current) => current + 1)
  }

  const pauseForInteraction = () => {
    setInteractionPaused(true)
    setCycleKey((current) => current + 1)
  }

  const resumeAfterInteraction = () => {
    setInteractionPaused(false)
    setCycleKey((current) => current + 1)
  }

  const renderSignalButton = (signal: (typeof operatingSignals)[number], index: number) => {
    const Icon = signal.icon
    const isActive = activeSignal === index
    return (
      <button
        aria-label={`${signal.label}. ${signal.detail}`}
        className={isActive ? 'is-active' : ''}
        data-cycle-active={isActive ? 'true' : undefined}
        key={signal.label}
        onClick={() => selectSignal(index)}
        onFocus={() => selectSignal(index)}
        onPointerEnter={() => selectSignal(index)}
        type="button"
      >
        <Icon />
        <span><strong>{signal.label}</strong><small>{signal.detail}</small></span>
        <ArrowUpRight />
        {isActive ? (
          <i
            aria-hidden="true"
            className="operating-progress-track"
            key={`${index}-${cycleKey}-${rotationPaused ? 'paused' : 'running'}`}
          />
        ) : null}
      </button>
    )
  }

  return (
    <section className="operating-dark" id="how-it-works">
      <div className="dark-heading">
        <span><Sparkles /> WAVE99 OPERATING LAYER</span>
        <h2>Transform the way your market becomes sellable</h2>
        <p>One accountable system for reconstruction, evidence-led outreach, sending safeguards, reply triage, and qualified handoff.</p>
        <a className={buttonLight} href="#platform">See the operating route <ArrowRight className="size-4" /></a>
      </div>
      <div className="operating-cycle-toolbar">
        <span aria-live="polite"><i />State {activeSignal + 1} of {operatingSignals.length}</span>
        {!reducedMotion ? (
          <button
            aria-label={cyclePaused ? 'Resume operating state rotation' : 'Pause operating state rotation'}
            aria-pressed={cyclePaused}
            onClick={() => {
              setCyclePaused((current) => !current)
              setCycleKey((current) => current + 1)
            }}
            type="button"
          >
            {cyclePaused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />}
            {cyclePaused ? 'Resume states' : 'Pause states'}
          </button>
        ) : (
          <span>Motion reduced</span>
        )}
      </div>
      <PremiumReveal className="operating-system-reveal">
      <div
        className="operating-system-visual"
        data-active-signal={activeSignal}
        data-cycle-paused={rotationPaused ? 'true' : 'false'}
        onBlurCapture={resumeAfterInteraction}
        onFocusCapture={pauseForInteraction}
        onPointerEnter={pauseForInteraction}
        onPointerLeave={resumeAfterInteraction}
      >
        <div className="system-rail rail-left">
          {operatingSignals.slice(0, 3).map((signal, index) => renderSignalButton(signal, index))}
        </div>
        <div className="system-core">
          <div className="system-core-ring ring-one" />
          <div className="system-core-ring ring-two" />
          <div className="system-core-ring ring-three" />
          <img alt="" src="/wave99-mark.png" />
          <AnimatePresence mode="wait">
            <m.span
              animate={{ opacity: 1, scale: 1, y: 0 }}
              initial={{ opacity: 0, scale: 0.96, y: 6 }}
              key={operatingSignals[activeSignal].status}
              transition={{ duration: 0.3, ease: [0, 0, 0.38, 0.9] }}
            >
              <small>Wave99 operating</small>
              <strong>{operatingSignals[activeSignal].status}</strong>
            </m.span>
          </AnimatePresence>
        </div>
        <div className="system-rail rail-right">
          {operatingSignals.slice(3).map((signal, index) => renderSignalButton(signal, index + 3))}
        </div>
      </div>
      </PremiumReveal>
      <KineticSignalField items={signalPills} />
      {proofCadence === 'testimonial-cadence-preview' ? (
        <TestimonialFramePreview slot="operating" />
      ) : proofCadence === 'operating-interludes' ? (
        <OperatingEvidenceInterlude />
      ) : (
        <blockquote className="dark-method-quote">
          <p>“The software is not another system your sellers must configure. It is the operating layer Wave99 uses to make the work visible, repeatable, and accountable.”</p>
          <footer><span><b>Designed for operated delivery</b><small>Human judgment at the commercial edges</small></span><img alt="" src="/wave99-mark.png" /></footer>
        </blockquote>
      )}
    </section>
  )
}

function CapabilityVisual({ index }: { index: number }) {
  if (index === 0) {
    return (
      <div className="capability-scene scene-map">
        <div className="map-search"><FileSearch /><span><small>MARKET QUERY</small><b>aseptic process systems · Northern Europe</b></span></div>
        <div className="map-nodes">
          {['Hawthorn', 'Northline', 'Vantage', 'Baltic Aseptic', 'Mero Process'].map((item, itemIndex) => (
            <span className={itemIndex === 0 ? 'is-active' : ''} key={item}><i /><b>{item}</b><small>{[84, 76, 71, 69, 64][itemIndex]}% confidence</small></span>
          ))}
        </div>
        <div className="map-source-card"><Radar /><span><small>SIGNAL ATTACHED</small><b>New line planned for Q4</b></span><em>Source 03</em></div>
      </div>
    )
  }
  if (index === 1) {
    return (
      <div className="capability-scene scene-message">
        <div className="message-evidence">
          <p><Check /> Approved validation proof</p>
          <p><Check /> Quality role signal</p>
          <p><Check /> Specification timing</p>
        </div>
        <div className="message-draft">
          <header><span><img alt="" src="/wave99-mark.png" />Wave99 draft</span><em>Evidence linked</em></header>
          <p>Noticed the new aseptic line and the quality hiring around it. We have a short validation note for teams setting requirements before the specification is fixed.</p>
          <footer><span>LinkedIn</span><span>Email</span><button type="button">Checked against brief</button></footer>
        </div>
      </div>
    )
  }
  if (index === 2) {
    return (
      <div className="capability-scene scene-health">
        <div className="health-ring"><Gauge /><strong>97</strong><small>sender health</small></div>
        <div className="health-controls">
          <p><span><ShieldCheck />Reply stop</span><b>Active</b></p>
          <p><span><Zap />Daily capacity</span><b>42 / 70</b></p>
          <p><span><Database />Suppression</span><b>Synced</b></p>
          <p><span><BrainCircuit />Human escalation</span><b>Assigned</b></p>
        </div>
      </div>
    )
  }
  return (
    <div className="capability-scene scene-handoff">
      <div className="handoff-thread">
        <p className="is-out">We have a short validation note for teams setting requirements now.</p>
        <p className="is-in">Send it and include our quality lead—we are setting them this month.</p>
      </div>
      <div className="handoff-packet">
        <header><CircleCheck /><span><small>QUALIFIED HANDOFF</small><b>Ready for your seller</b></span></header>
        <p><span>Source</span><b>3 attached</b></p>
        <p><span>Owner</span><b>Technical seller</b></p>
        <p><span>Next step</span><b>Send note + confirm date</b></p>
      </div>
    </div>
  )
}

function DeliveryPrincipleInterlude() {
  return (
    <aside
      aria-labelledby="delivery-principle-title"
      className="delivery-principle-interlude"
      data-proof-kind="delivery-standard"
    >
      <div>
        <span><ShieldCheck aria-hidden="true" /> OPERATED DELIVERY STANDARD</span>
        <h3 id="delivery-principle-title">
          Your team gets the outcome, not another workflow to babysit.
        </h3>
      </div>
      <div className="delivery-principle-detail">
        <p>
          Wave99 operates the difficult middle while your team keeps the
          decisions that require market judgment.
        </p>
        <ul aria-label="Operated delivery boundaries">
          <li><Check aria-hidden="true" /> Controls remain visible</li>
          <li><Check aria-hidden="true" /> Claims stay inside the brief</li>
          <li><Check aria-hidden="true" /> Human judgment owns the edges</li>
        </ul>
        <small>Operating principle · not a customer testimonial</small>
      </div>
    </aside>
  )
}

export function PlatformSection({
  proofCadence,
  testimonialStyle = 'standard',
}: {
  proofCadence: ProofCadenceConcept | 'control'
  testimonialStyle?: 'standard' | 'combined-white'
}) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inactiveOrder = capabilityTabs
    .map((_item, index) => index)
    .filter((index) => index !== selectedIndex)

  return (
    <section
      className="platform-section"
      data-sector="localhost-4215-platform"
      id="platform"
    >
      <div className="platform-heading">
        <h2>One operating system for the difficult middle</h2>
        <p>Give your team the outcome without giving them another daily workflow to manage.</p>
      </div>
      <TabGroup
        className="capability-stack capability-premium-deck"
        onChange={setSelectedIndex}
        selectedIndex={selectedIndex}
      >
        <TabList aria-label="Wave99 operating capabilities" className="capability-tab-stack">
          {capabilityTabs.map((item, index) => {
            const stackRank = index === selectedIndex ? capabilityTabs.length - 1 : inactiveOrder.indexOf(index)
            return (
              <Tab
                aria-label={`View ${item.label}`}
                className="capability-stack-tab"
                key={item.id}
                style={{
                  '--stack-index': index,
                  '--stack-rank': stackRank,
                } as CSSProperties}
              >
                <span><i />{item.label}</span>
                <span className="capability-tab-action">Open <ArrowUpRight /></span>
              </Tab>
            )
          })}
        </TabList>
        <TabPanels className="capability-panel-shell">
          {capabilityTabs.map((item, index) => (
            <TabPanel className="capability-panel" key={item.id}>
              <m.div
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="capability-panel-motion"
                initial={{ opacity: 0, scale: 0.985, y: 16 }}
                transition={{ duration: 0.52, ease: [0, 0, 0.3, 1] }}
              >
                  <div className="capability-copy">
                    <p>{item.kicker}</p>
                    <h3>{item.title}</h3>
                    <span>{item.body}</span>
                    <b><CircleCheck />{item.outcome}</b>
                  </div>
                  <CapabilityVisual index={index} />
              </m.div>
            </TabPanel>
          ))}
        </TabPanels>
        <p className="capability-live-label" role="status"><i /> Viewing {capabilityTabs[selectedIndex].label}</p>
      </TabGroup>
      {proofCadence === 'testimonial-cadence-preview' ? (
        <TestimonialFramePreview slot="platform" />
      ) : proofCadence === 'operating-interludes' ? (
        <DeliveryPrincipleInterlude />
      ) : (
        <blockquote
          className={`platform-quote${testimonialStyle === 'combined-white' ? ' platform-quote-combined' : ''}`}
          data-proof-kind={testimonialStyle === 'combined-white' ? 'operating-principle' : undefined}
        >
          <p>“The work becomes useful when the account source, commercial interpretation, message, reply, and owner remain connected.”</p>
          <footer>
            {testimonialStyle === 'combined-white' ? (
              <span aria-hidden="true" className="platform-quote-emblem" />
            ) : null}
            <span><b>Wave99 evidence chain</b><small>Representative operating principle</small></span>
            {testimonialStyle === 'combined-white' ? <strong>WAVE99</strong> : null}
          </footer>
        </blockquote>
      )}
    </section>
  )
}

function CampaignBand({ onReview }: { onReview: (source: ReviewSource) => void }) {
  return (
    <section className="campaign-band">
      <div aria-hidden="true" className="campaign-radiance" />
      <div className="relative z-10">
        <h2>BUILD YOUR<br /><span>REAL MARKET</span><br />MOTION</h2>
        <button className={buttonLight} onClick={() => onReview('campaign')} type="button">Review your market <ArrowRight className="size-4" /></button>
        <p>Direction from your team · operated by Wave99 · qualified handoff back</p>
      </div>
    </section>
  )
}

function PersonaSection() {
  return (
    <section className="persona-section" id="why-wave99">
      <div className="persona-heading">
        <h2>Built to return value to the people who sell</h2>
      </div>
      <TabGroup className="persona-shell">
        <TabList className="persona-tabs">
          {personas.map((persona) => (
            <Tab className="persona-tab" key={persona.label}>
              <span>{persona.label}</span>
              <i />
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {personas.map((persona) => (
            <TabPanel className="persona-panel" key={persona.label}>
              <div className="persona-copy">
                <p>{persona.label}</p>
                <h3>{persona.title}</h3>
                <span>{persona.body}</span>
                <a href="#final-cta">See the handoff route <ArrowRight /></a>
              </div>
              <div className="persona-outcomes">
                {persona.outcomes.map(([title, body], index) => (
                  <article key={title}>
                    <small>0{index + 1}</small>
                    <h4>{title}</h4>
                    <p>{body}</p>
                  </article>
                ))}
              </div>
            </TabPanel>
          ))}
        </TabPanels>
      </TabGroup>
    </section>
  )
}

function OutputsSection() {
  const [flippedTile, setFlippedTile] = useState<number | null>(null)
  const outputInputType = useRef<string | null>(null)
  const hoverIntentTimer = useRef<number | null>(null)

  const setDoorWithIntent = (index: number | null) => {
    if (hoverIntentTimer.current !== null) window.clearTimeout(hoverIntentTimer.current)
    hoverIntentTimer.current = window.setTimeout(() => {
      setFlippedTile(index)
      hoverIntentTimer.current = null
    }, 82)
  }

  useEffect(() => () => {
    if (hoverIntentTimer.current !== null) window.clearTimeout(hoverIntentTimer.current)
  }, [])

  return (
    <section className="outputs-section" id="proof">
      <div className="outputs-heading">
        <h2>Representative work.<br />No invented results.</h2>
        <p>Until verified customer proof is approved for publication, Wave99 shows the work, labels sample data, and keeps method separate from outcome claims.</p>
      </div>
      <div className="output-mosaic">
        {outputTiles.map((tile, index) => {
          const opensLeft = [0, 3, 4, 6].includes(index)
          return (
            <article
              className={`output-flip-card door-${opensLeft ? 'left' : 'right'} tone-${tile.tone}${
                'wide' in tile && tile.wide ? ' is-wide' : ''
              }${flippedTile === index ? ' is-flipped' : ''}`}
              data-door-direction={opensLeft ? 'left' : 'right'}
              data-door-state={flippedTile === index ? 'open' : 'closed'}
              key={`${tile.label}-${index}`}
            >
            <button
              aria-label={`${tile.label}. ${flippedTile === index ? 'Show summary' : 'Inspect evidence note'}`}
              aria-pressed={flippedTile === index}
              onClick={() => {
                setFlippedTile((current) =>
                  outputInputType.current !== 'keyboard' &&
                  outputInputType.current !== 'touch' &&
                  outputInputType.current !== 'pen'
                    ? index
                    : current === index
                      ? null
                      : index,
                )
                outputInputType.current = null
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  outputInputType.current = 'keyboard'
                }
              }}
              onPointerDown={(event) => {
                outputInputType.current = event.pointerType
              }}
              onPointerEnter={(event) => {
                if (event.pointerType === 'mouse') setDoorWithIntent(index)
              }}
              onPointerLeave={(event) => {
                if (event.pointerType === 'mouse') {
                  const mosaic = event.currentTarget.closest('.output-mosaic')
                  const next = event.relatedTarget
                  if (!(next instanceof Node) || !mosaic?.contains(next)) {
                    setDoorWithIntent(null)
                  }
                }
              }}
              type="button"
            >
              <span className="output-flip-inner">
                <span className="output-flip-face output-flip-front">
                  <strong>{tile.value}</strong>
                  <span>{tile.label}</span>
                  <small>{tile.note}</small>
                  <ArrowUpRight aria-hidden="true" />
                </span>
                <span className="output-flip-face output-flip-back">
                  <small>Evidence note · representative</small>
                  <strong>{tile.note}</strong>
                  <span>Source, review state, and accountable owner remain attached.</span>
                  <em>Return to summary <ArrowRight /></em>
                </span>
              </span>
            </button>
            </article>
          )
        })}
      </div>
    </section>
  )
}

function InsightVisual({ tone }: { tone: string }) {
  return (
    <div aria-hidden="true" className={`insight-visual tone-${tone}`}>
      <span /><span /><span />
      <i /><i />
      <img alt="" src="/wave99-mark.png" />
    </div>
  )
}

function InsightsSection() {
  return (
    <section className="insights-section" id="insights">
      <div className="insights-heading">
        <h2>Read the market before you automate it</h2>
        <a href="#final-cta">View the operating method <ArrowRight /></a>
      </div>
      <div className="insight-grid">
        {insightCards.map((card) => (
          <article key={card.title}>
            <InsightVisual tone={card.tone} />
            <p>{card.category}<span>·</span>{card.date}</p>
            <h3>{card.title}</h3>
            <a aria-label={`Read ${card.title}`} href="#final-cta"><ArrowUpRight /></a>
          </article>
        ))}
      </div>
    </section>
  )
}

function StandardsSection() {
  return (
    <section className="standards-section">
      <div className="standards-heading">
        <p>THE TRUST LAYER · MOTION YOU CAN PAUSE</p>
        <h2>A wall of operating proof, until customer proof is approved</h2>
        <span>
          The premium testimonial-wall mechanic is preserved without inventing praise. Every card names a standard the team can inspect.
        </span>
      </div>
      <OperatingProofWall items={operatingStandards} />
    </section>
  )
}

export function FinalSection({ onReview }: { onReview: (source: ReviewSource) => void }) {
  return (
    <section className="final-section" id="final-cta">
      <div className="final-radiance" aria-hidden="true" />
      <div className="final-cta-content">
        <p>YOUR MARKET · OPERATED BY WAVE99</p>
        <h2>UNLOCK THE MARKET<br /><span>YOUR CRM CAN’T SEE</span></h2>
        <button className={buttonLight} onClick={() => onReview('final')} type="button">Review your market <ArrowRight className="size-4" /></button>
        <small>Review the accounts, operating plan, safeguards, and handoff flow in one focused session.</small>
      </div>
      <footer className="site-footer">
        <div className="footer-top">
          <Brand inverted />
          <p>Done-for-you Human + AI outreach for hard-to-read niche B2B markets.</p>
          <span><a href="#top">Back to top <ArrowUpRight /></a></span>
        </div>
        <div className="footer-links">
          <div><h3>Product</h3><a href="#portal">Wave99 portal</a><a href="#platform">Market reconstruction</a><a href="#platform">Outreach production</a><a href="#platform">Sending operations</a><a href="#platform">Reply intelligence</a></div>
          <div><h3>Why Wave99</h3><a href="#markets">Hard-to-read markets</a><a href="#how-it-works">Operated delivery</a><a href="#proof">Evidence standards</a><a href="#why-wave99">For your team</a></div>
          <div><h3>Resources</h3><a href="#insights">Market notes</a><a href="#insights">Operating method</a><a href="#proof">Representative proof</a><a href="#final-cta">Review your market</a></div>
          <div><h3>Access</h3><a href="https://app.wave99.com">Client access <ExternalLink /></a><button onClick={() => onReview('final')} type="button">Market review</button><span>Prototype · no production data</span></div>
        </div>
        <div className="footer-bottom"><span>© 2026 Wave99</span><span>Privacy</span><span>Terms</span><span>Human accountability</span><span>Evidence before automation</span></div>
      </footer>
    </section>
  )
}

export default function App() {
  const [reviewOpen, setReviewOpen] = useState(false)
  const [reviewSource, setReviewSource] = useState<ReviewSource>('hero')
  const [email, setEmail] = useState('')
  const pageRef = useRef<HTMLDivElement>(null)
  const proofCadence = getProofCadenceConcept()

  const openReview = (source: ReviewSource) => {
    setReviewSource(source)
    setReviewOpen(true)
  }

  return (
    <LazyMotion features={domAnimation} strict>
      <div className="site-shell" data-proof-cadence={proofCadence} ref={pageRef}>
        <a className="skip-link" href="#main">Skip to content</a>
        <SiteHeader onReview={openReview} />
        <main id="main">
          <Hero email={email} onReview={openReview} setEmail={setEmail} />
          {proofCadence === 'testimonial-cadence-preview' ? (
            <TestimonialFramePreview slot="portal" />
          ) : null}
          <ResultsFirstSection onReview={openReview} />
          {proofCadence === 'testimonial-cadence-preview' ? (
            <TestimonialFramePreview slot="commonView" />
          ) : null}
          <DarkOperatingSection proofCadence={proofCadence} />
          <PlatformSection proofCadence={proofCadence} />
          <CampaignBand onReview={openReview} />
          <PersonaSection />
          {proofCadence === 'testimonial-cadence-preview' ? (
            <TestimonialFramePreview slot="seller" />
          ) : null}
          {proofCadence === 'testimonial-cadence-preview' ? (
            <CustomerResultsPreview />
          ) : (
            <OutputsSection />
          )}
          <InsightsSection />
          {proofCadence === 'testimonial-cadence-preview' ? (
            <CustomerLovePreview />
          ) : (
            <StandardsSection />
          )}
          <FinalSection onReview={openReview} />
        </main>
        <ReviewDialog
          initialEmail={email}
          onClose={() => setReviewOpen(false)}
          open={reviewOpen}
          source={reviewSource}
        />
      </div>
    </LazyMotion>
  )
}
